import createClient from "openapi-fetch";

import { env } from "@/env";

export interface ModuleInfo {
  key: string;
  name: string;
  sequence: number;
}

export interface ProjectInfo {
  id: string;
  name: string;
  code: string | null;
  status: string;
}

export interface CurrentUser {
  id: string;
  email: string | null;
  name: string | null;
  tenant_id: string | null;
  tenant_name?: string | null;
  roles: string[];
  permissions: string[];
  project_access: Record<string, string[]>;
  modules: ModuleInfo[];
  projects: ProjectInfo[];
}

export interface HealthStatus {
  status: "healthy" | "degraded";
  version: string;
  environment: string;
  dependencies: Record<string, { status: "up" | "down"; latency_ms: number | null }>;
}

let tokenProvider: () => Promise<string | undefined> = async () => undefined;

export function setTokenProvider(fn: () => Promise<string | undefined>): void {
  tokenProvider = fn;
}

let onUnauthorized: () => void = () => {};
let unauthorizedHandled = false;

/**
 * Called when the API returns 401 (the session has expired). Wired to the
 * Auth0 logout in App.vue so a dead session doesn't leave the user stuck
 * "logged in" with every request failing.
 */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/**
 * `fetch` wrapper for calls to OUR API: a 401 means the session expired, so
 * trigger logout (once) and abort. Direct-to-S3 uploads use plain `fetch` —
 * a 401 there is not a session problem.
 */
async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const response = await window.fetch(input, init);
  if (response.status === 401) {
    if (!unauthorizedHandled) {
      unauthorizedHandled = true;
      onUnauthorized();
    }
    throw new Error("Your session has expired — signing you out.");
  }
  return response;
}

const baseClient = createClient({ baseUrl: env.apiBaseUrl });

baseClient.use({
  async onRequest({ request }) {
    const token = await tokenProvider();
    if (token) request.headers.set("authorization", `Bearer ${token}`);
    return request;
  },
});

export async function getMe(): Promise<CurrentUser> {
  const response = await apiFetch(`${env.apiBaseUrl}/me`, {
    headers: {
      authorization: `Bearer ${await tokenProvider()}`,
    },
  });
  if (!response.ok) {
    throw new Error(`/me returned ${response.status}`);
  }
  return response.json();
}

export async function getHealth(): Promise<HealthStatus> {
  const response = await apiFetch(`${env.apiBaseUrl}/healthz`);
  return response.json();
}

// ---------- Read endpoints (Module 1 Phase A6) ----------

async function apiGet<T>(path: string): Promise<T> {
  const response = await apiFetch(`${env.apiBaseUrl}${path}`, {
    headers: { authorization: `Bearer ${await tokenProvider()}` },
  });
  if (!response.ok) {
    throw new Error(`${path} returned ${response.status}`);
  }
  return response.json() as Promise<T>;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const response = await apiFetch(`${env.apiBaseUrl}${path}`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${await tokenProvider()}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`${path} returned ${response.status}`);
  }
  return response.json() as Promise<T>;
}

// Build an Error from a response, preferring the API's `{detail}` message
// (e.g. the role lock-out guard) over a bare status code.
async function detailedError(response: Response, path: string): Promise<Error> {
  try {
    const body = (await response.json()) as { detail?: string };
    if (typeof body?.detail === "string") return new Error(body.detail);
  } catch {
    /* fall back to status below */
  }
  return new Error(`${path} returned ${response.status}`);
}

async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const response = await apiFetch(`${env.apiBaseUrl}${path}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${await tokenProvider()}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await detailedError(response, path);
  return response.json() as Promise<T>;
}

export interface SiteDiarySummary {
  id: string;
  project_id: string;
  project_name: string;
  project_code: string | null;
  diary_date: string;
  rail_week: string | null;
  status: string;
  submitted_at: string | null;
}

export interface SiteDiaryDetail extends SiteDiarySummary {
  client_name: string | null;
  site_description: string | null;
  shift_start: string | null;
  shift_end: string | null;
  contract_po_number: string | null;
  staff: Array<Record<string, unknown>>;
  entries: Array<Record<string, unknown>>;
  fields: Record<string, unknown>;
  photos: Array<Record<string, unknown>>;
  drawings: Array<Record<string, unknown>>;
  created_at: string;
  created_by_name: string | null;
  submitted_by: string | null;
  submitted_by_name: string | null;
  submitted_signature: string | null;
  submitted_gps: Record<string, number> | null;
  works_summary: string | null;
  works_summary_at: string | null;
  works_summary_model: string | null;
  approved_at: string | null;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_signature: string | null;
  client_accepted_at: string | null;
  client_accepted_by_name: string | null;
  client_accepted_signature: string | null;
  rejection_reason: string | null;
  last_updated_at: string | null;
  last_updated_by_name: string | null;
  qa_entries: Array<Record<string, unknown>>;
  approver_role: string | null;
  approver_emails: string[];
}

export interface DiaryHistoryEntry {
  seq: number;
  event_type: string;
  label: string;
  actor_name: string | null;
  at: string;
}

export interface StaffBriefingSummary {
  id: string;
  project_name: string;
  project_code: string | null;
  briefing_date: string;
  rail_week: string | null;
  delivered: boolean;
  status: string;
  work_pack_id: string | null;
  signon_count: number;
}

export interface StaffBriefingDetail extends StaffBriefingSummary {
  project_id: string;
  diary_id: string | null;
  task_description: string | null;
  briefed_content: WorkPackTaskBrief | null;
  principal_contractor: string | null;
  responsible_contractor: string | null;
  briefer: Record<string, unknown>;
  delivered_at: string | null;
  delivered_signature: string | null;
  delivered_by_name: string | null;
  signons: Array<Record<string, unknown>>;
  closed_at: string | null;
  closed_by_name: string | null;
  created_at: string;
  created_by_name: string | null;
  last_updated_at: string | null;
  last_updated_by_name: string | null;
  // Briefing recording + AI coverage.
  recording_file_id: string | null;
  recording_mime: string | null;
  recording_duration_seconds: number | null;
  recording_recorded_at: string | null;
  recording_retention_until: string | null;
  recording_purged: boolean;
  transcription_status: string; // none | pending | done | failed
  transcription_error: string | null;
  transcript: string | null;
  transcribed_at: string | null;
  coverage: BriefingCoverageTopic[] | null;
  coverage_summary: string | null;
  coverage_assessed_at: string | null;
}

export interface BriefingCoverageTopic {
  topic: string;
  covered: boolean;
  evidence: string | null;
}

export interface AttachRecordingResult {
  status: string;
  retention_until: string;
  transcription_enabled: boolean;
}

export const attachBriefingRecording = (
  briefingId: string,
  body: { file_id: string; duration_seconds?: number },
): Promise<AttachRecordingResult> =>
  apiPost(`/staff-briefings/${briefingId}/recording`, body);

export const recheckBriefingCoverage = (
  briefingId: string,
): Promise<{ topics: BriefingCoverageTopic[]; summary: string | null }> =>
  apiPost(`/staff-briefings/${briefingId}/coverage/recheck`, {});

export interface BriefingReviewFinding {
  severity: "warning" | "suggestion";
  message: string;
}

/** Pre-delivery consistency review (Phase 3): sign-ons without a signature, an
 *  undelivered briefing, no work pack linked, unevidenced topics. Advisory. */
export const reviewBriefing = (
  briefingId: string,
): Promise<{ ok: boolean; findings: BriefingReviewFinding[] }> =>
  apiPost(`/staff-briefings/${briefingId}/review`, {});

export interface BriefingHistoryEntry {
  seq: number;
  event_type: string;
  label: string;
  actor_name: string | null;
  at: string;
}

export interface DefectRow {
  id: string;
  diary_id: string | null;
  project_id: string | null;
  check_sheet_id: string | null;
  source: string;
  description: string;
  category: string;
  risk_rating: string | null;
  priority: string | null;
  target_date: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  location_ref: string | null;
  structure_id: string | null;
  status: string;
  is_snag: boolean;
  issue_type: string;
  trade: string | null;
  photo_ids: string[];
  raised_by: string;
  raised_by_name: string | null;
  raised_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolved_by_name: string | null;
  resolution_notes: string | null;
  comments: Array<Record<string, unknown>>;
  attachments: Array<Record<string, unknown>>;
  watchers: Array<Record<string, unknown>>;
  related: Array<Record<string, unknown>>;
  last_updated_at: string;
  last_updated_by_name: string | null;
}

export interface DefectHistoryEntry {
  seq: number;
  event_type: string;
  label: string;
  actor_name: string | null;
  at: string;
  detail: string | null;
}

export interface ObservationRow {
  id: string;
  diary_id: string | null;
  project_id: string | null;
  kind: string;
  description: string;
  observation_type: string | null;
  location_ref: string | null;
  status: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  action_taken: string | null;
  photo_ids: string[];
  raised_by: string;
  raised_by_name: string | null;
  raised_at: string;
  closed_at: string | null;
  closed_by_name: string | null;
  last_updated_at: string;
  last_updated_by_name: string | null;
}

export interface ObservationHistoryEntry {
  seq: number;
  event_type: string;
  label: string;
  actor_name: string | null;
  at: string;
}

/** Append `?project_id=` when a project context is set. */
function projectQuery(projectId?: string | null): string {
  return projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
}

// ---------- Projects (platform navigation context) ----------

export interface ProjectDetail extends ProjectInfo {
  client_name: string | null;
  service_area: string | null;
  created_at: string;
  // Approval routing per document type (brief §1.5), e.g. {"site_diary": "cre"}.
  approval_policy: Record<string, string>;
  // Tasks-per-location denominator for the progress dashboard (brief §1.6).
  tasks_per_location: number | null;
  // Optional per-structure-type task counts (brief §1.6).
  progress_task_lists: Record<string, number>;
  // Optional per-structure-type named ITP template lists (brief §1.6).
  progress_task_templates: Record<string, string[]>;
  // Approver email recipients per role, notified on submission (brief §1.5).
  notification_emails: Record<string, string[]>;
  // Site location (set in Settings via postcode lookup) — powers live weather.
  postcode: string | null;
  location_name: string | null;
  latitude: number | null;
  longitude: number | null;
  // Site-specific hazards (incl. archived — each carries archived_at).
  hazards: ProjectHazard[];
}

export interface ProjectHazard {
  hazard_id: string;
  category: string;
  location: string | null;
  description: string;
  added_at: string | null;
  added_by_name: string | null;
  archived_at: string | null;
}

export interface LocationProgress {
  id: string;
  ref: string;
  name: string | null;
  structure_type: string | null;
  area: string | null;
  status: "not_started" | "in_progress" | "complete";
  tasks_done: number;
  tasks_total: number;
  open_issues: number;
  outstanding: Array<{ template_id: string; name: string }>;
}

export interface ProjectProgress {
  total_locations: number;
  tasks_per_location: number | null;
  task_lists: Record<string, number>;
  locations_not_started: number;
  locations_in_progress: number;
  locations_complete: number;
  total_tasks: number;
  tasks_complete: number;
  percent_complete: number;
  open_issues_total: number;
  locations_with_open_issues: number;
  remaining_tasks: number;
  velocity_per_week: number;
  velocity_all_time: number;
  is_stalled: boolean;
  forecast_complete_date: string | null;
  trend: Array<{
    week: string;
    tasks_completed: number;
    cumulative_tasks: number;
    cumulative_percent: number;
  }>;
  locations: LocationProgress[];
}

/** Set the per-document-type approver role for a project (brief §1.5). */
export const updateProjectApprovalPolicy = (
  projectId: string,
  policy: Record<string, string>,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectApprovalPolicyUpdated",
      payload: { policy },
    },
  ]);

/** Live project progress — locations tracked against their task list (§1.6). */
export const getProjectProgress = (
  projectId: string,
): Promise<ProjectProgress> => apiGet(`/projects/${projectId}/progress`);

/** Set the progress-dashboard denominators (§1.6) — the project-level number,
 *  optional per-structure-type counts, and optional named template lists. */
export const updateProjectProgressConfig = (
  projectId: string,
  tasksPerLocation: number,
  taskLists?: Record<string, number>,
  taskTemplates?: Record<string, string[]>,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectProgressConfigUpdated",
      payload: {
        tasks_per_location: tasksPerLocation,
        ...(taskLists ? { task_lists: taskLists } : {}),
        ...(taskTemplates ? { task_templates: taskTemplates } : {}),
      },
    },
  ]);

/** Fetch the progress report PDF / CSV as a blob (auth-gated). */
export async function fetchProgressExport(
  projectId: string,
  format: "pdf" | "csv",
): Promise<Blob> {
  const response = await apiFetch(
    `${env.apiBaseUrl}/projects/${projectId}/progress/${format}`,
    { headers: { authorization: `Bearer ${await tokenProvider()}` } },
  );
  if (!response.ok) throw new Error(`Progress ${format} returned ${response.status}`);
  return response.blob();
}

/** Set the approver email recipients per role, notified on submission (§1.5). */
export const updateProjectNotificationRecipients = (
  projectId: string,
  recipients: Record<string, string[]>,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectNotificationRecipientsUpdated",
      payload: { recipients },
    },
  ]);

// ---- Project site location + live weather ----

/** A postcode resolved to coordinates + a human place name. */
export interface PostcodeLookup {
  postcode: string;
  latitude: number;
  longitude: number;
  place_name: string;
}

/** Resolve a UK postcode to coordinates + place name via postcodes.io
 *  (free, no API key). Throws if the postcode isn't found. */
export async function lookupPostcode(postcode: string): Promise<PostcodeLookup> {
  const trimmed = postcode.trim();
  const resp = await fetch(
    `https://api.postcodes.io/postcodes/${encodeURIComponent(trimmed)}`,
  );
  if (!resp.ok) {
    throw new Error(resp.status === 404 ? "Postcode not found" : "Lookup failed");
  }
  const body = (await resp.json()) as {
    result: {
      postcode: string;
      latitude: number;
      longitude: number;
      admin_district?: string;
      parish?: string;
      admin_ward?: string;
    };
  };
  const r = body.result;
  const place = r.admin_ward ?? r.parish ?? r.admin_district ?? r.postcode;
  return {
    postcode: r.postcode,
    latitude: r.latitude,
    longitude: r.longitude,
    place_name: r.admin_district ? `${place}, ${r.admin_district}` : place,
  };
}

/** Set (or clear) the project's site location. Pass nulls to clear. */
export const updateProjectLocation = (
  projectId: string,
  location: {
    postcode: string | null;
    location_name: string | null;
    latitude: number | null;
    longitude: number | null;
  },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectLocationUpdated",
      payload: location,
    },
  ]);

export interface WeatherNow {
  temperature: number | null;
  wind_speed?: number | null;
  precipitation?: number | null;
  label: string;
  emoji: string;
}

export interface WeatherDay {
  date: string;
  label: string;
  emoji: string;
  temp_max: number | null;
  temp_min: number | null;
  precip_prob: number | null;
}

export interface ProjectWeather {
  location_name: string | null;
  current: WeatherNow | null;
  forecast: WeatherDay[];
}

/** Live + 7-day forecast weather for the project's site location (Open-Meteo).
 *  Returns null if no location is set or the upstream call failed. */
export const getProjectWeather = (
  projectId: string,
): Promise<ProjectWeather | null> => apiGet(`/projects/${projectId}/weather`);

export const listProjects = (): Promise<ProjectDetail[]> => apiGet("/projects");

// ---- Semantic search + ask the project ----
export interface SearchHit {
  source_type: string;
  source_id: string;
  title: string;
  snippet: string;
  score: number;
}
export interface SearchResponse {
  enabled: boolean;
  results: SearchHit[];
}
export interface AskCitation {
  n: number;
  source_type: string;
  source_id: string;
  title: string;
}
export interface AskResponse {
  answer: string | null;
  model: string | null;
  citations: AskCitation[];
}

export const searchProject = (
  projectId: string,
  q: string,
): Promise<SearchResponse> =>
  apiGet(`/projects/${projectId}/search?q=${encodeURIComponent(q)}`);

export const askProject = (
  projectId: string,
  question: string,
): Promise<AskResponse> =>
  apiPost(`/projects/${projectId}/ask`, { question });

export const getProject = (id: string): Promise<ProjectDetail> =>
  apiGet(`/projects/${id}`);

/** Edit a project's details (partial; events flow through /sync/events). */
export const updateProject = (
  id: string,
  fields: {
    name?: string;
    code?: string;
    client_name?: string;
    service_area?: string;
  },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: id,
      event_type: "ProjectUpdated",
      payload: fields,
    },
  ]);

/** One-time idempotent beta seed: creates MMA-122 with the legacy id. */
export const ensureDefaultProject = (): Promise<ProjectDetail> =>
  apiPost("/projects/ensure-default", {});

/** Create a project; returns the new id (events flow through /sync/events). */
export async function createProject(fields: {
  name: string;
  client_name?: string;
  code?: string;
}): Promise<string> {
  const id = crypto.randomUUID();
  await pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: id,
      event_type: "ProjectCreated",
      payload: { ...fields, status: "active" },
    },
  ]);
  return id;
}

const projectLifecycle =
  (eventType: string) =>
  (id: string): Promise<void> =>
    pushEvents([
      { aggregate_type: "project", aggregate_id: id, event_type: eventType, payload: {} },
    ]);

export const archiveProject = projectLifecycle("ProjectArchived");
export const restoreProject = projectLifecycle("ProjectRestored");
export const deleteProject = projectLifecycle("ProjectDeleted");

/** Add a site-specific hazard to the project's register. */
export const addProjectHazard = (
  projectId: string,
  hazard: { category: string; location?: string; description: string },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectHazardAdded",
      payload: {
        hazard_id: crypto.randomUUID(),
        category: hazard.category,
        description: hazard.description,
        ...(hazard.location ? { location: hazard.location } : {}),
      },
    },
  ]);

export const archiveProjectHazard = (
  projectId: string,
  hazardId: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectHazardArchived",
      payload: { hazard_id: hazardId },
    },
  ]);

// ---------- Activity (project event-log timeline) ----------

export interface ActivityItem {
  id: string;
  occurred_at: string;
  actor_name: string;
  aggregate_type: string;
  event_type: string;
  title: string;
  detail: string | null;
}

export const listActivity = (projectId: string): Promise<ActivityItem[]> =>
  apiGet(`/projects/${projectId}/activity`);

// ---------- Dashboard overview (KPIs + cached AI status) ----------

export interface ProjectKpis {
  open_defects: number;
  open_snags: number;
  open_observations: number;
  inspections_pending: number;
  inspections_in_progress: number;
  diaries_last_7_days: number;
  diaries_awaiting_approval: number;
}
export interface AiStatus {
  text: string | null;
  model: string | null;
  generated_at: string | null;
  stale: boolean;
}
export interface ProjectOverview {
  project_id: string;
  project_name: string;
  kpis: ProjectKpis;
  ai_status: AiStatus;
  weather: ProjectWeather | null;
  watch: WatchItem[];
}
export interface ProjectWeather {
  diary_date: string;
  conditions: string | null;
  temperature: string | null;
  impact: string | null;
}
export interface WatchItem {
  label: string;
  date: string;
  kind: string;
  overdue: boolean;
  target_route: string;
  target_id: string | null;
}

export const getProjectOverview = (
  projectId: string,
): Promise<ProjectOverview> => apiGet(`/projects/${projectId}/overview`);

export const refreshProjectAiStatus = (
  projectId: string,
): Promise<AiStatus> =>
  apiPost(`/projects/${projectId}/overview/ai-status`, {});

// ---------- Notifications (actionable subset of activity) ----------

export interface NotificationItem {
  kind: string;
  title: string;
  detail: string | null;
  target_route: string;
  target_id: string;
  since: string | null;
  approver_role: string | null;
}

export const listNotifications = (
  projectId: string,
): Promise<NotificationItem[]> =>
  apiGet(`/projects/${projectId}/notifications`);

// ---------- Locations (structures, under a project) ----------

export interface LocationInfo {
  id: string;
  project_id: string;
  ref: string;
  name: string | null;
  structure_type: string | null;
  area: string | null;
  status: string;
  created_at: string;
}

export const listLocations = (
  projectId?: string | null,
): Promise<LocationInfo[]> =>
  apiGet(`/locations${projectQuery(projectId)}`);

/** Manually register a single site/structure (vs the bulk import). */
export const createLocation = (location: {
  project_id: string;
  ref: string;
  name?: string;
  structure_type?: string;
  area?: string;
}): Promise<LocationInfo> => apiPost("/locations", location);

export interface LocationImportResult {
  locations_imported: number;
}

/** Upload a structures workbook and register its locations for the project. */
export async function importLocations(
  projectId: string,
  file: File,
): Promise<LocationImportResult> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiFetch(
    `${env.apiBaseUrl}/projects/${projectId}/locations/import`,
    {
      method: "POST",
      headers: { authorization: `Bearer ${await tokenProvider()}` },
      body: form,
    },
  );
  if (!response.ok) throw new Error(`Location import failed (${response.status})`);
  return response.json() as Promise<LocationImportResult>;
}

export const listSiteDiaries = (
  projectId?: string | null,
): Promise<SiteDiarySummary[]> =>
  apiGet(`/site-diaries${projectQuery(projectId)}`);

/**
 * Office pre-population: create a site diary for a shift (brief §1.2).
 * Returns the new diary id so the caller can open it.
 */
export interface RosterGangMember {
  person_id: string | null;
  name: string;
  role: string | null;
  shift: string | null;
  project_code: string | null;
}

/** The gang rostered for a diary's date/project — candidates to pre-populate. */
export const getDiaryRosterGang = (
  diaryId: string,
): Promise<RosterGangMember[]> =>
  apiGet(`/site-diaries/${diaryId}/roster-gang`);

/** A field value proposed by carry-forward, with where it came from. */
export interface CarryForwardField {
  field_path: string;
  label: string;
  value: unknown;
  source: string;
}

export interface CarryForwardProposal {
  prior_diary_id: string | null;
  prior_diary_date: string | null;
  has_roster: boolean;
  fields: CarryForwardField[];
}

/** Propose values to seed a diary from the previous diary + the roster
 *  (Phase 1 carry-forward). Project + date scoped, read-only. */
export const getDiaryCarryForward = (params: {
  projectId: string;
  date: string;
  projectCode?: string | null;
}): Promise<CarryForwardProposal> => {
  const q = new URLSearchParams({
    project_id: params.projectId,
    date: params.date,
  });
  if (params.projectCode) q.set("project_code", params.projectCode);
  return apiGet(`/site-diaries/carry-forward?${q.toString()}`);
};

/** Apply chosen carry-forward field values to a diary (one event per field). */
export const applyDiaryFields = (
  diaryId: string,
  fields: Array<{ field_path: string; value: unknown }>,
): Promise<void> =>
  pushEvents(
    fields.map((f) => ({
      aggregate_type: "site_diary",
      aggregate_id: diaryId,
      event_type: "SiteDiaryFieldUpdated",
      payload: { field_path: f.field_path, value: f.value },
    })),
  );

/** Set the diary's allocated staff (last-write-wins full list). */
export const allocateDiaryStaff = (
  diaryId: string,
  staff: Array<{ person_id?: string | null; name: string; role?: string | null }>,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "site_diary",
      aggregate_id: diaryId,
      event_type: "SiteDiaryStaffAllocated",
      payload: {
        staff: staff.map((s) => ({
          name: s.name,
          ...(s.person_id ? { person_id: s.person_id } : {}),
          ...(s.role ? { role: s.role } : {}),
        })),
      },
    },
  ]);

export async function createSiteDiary(diary: {
  project_id: string;
  project_name: string;
  project_code?: string;
  client_name?: string;
  site_description?: string;
  diary_date: string;
  rail_week?: string;
  shift_start?: string;
  shift_end?: string;
  contract_po_number?: string;
}): Promise<string> {
  const diaryId = crypto.randomUUID();
  const payload: Record<string, unknown> = {
    project_id: diary.project_id,
    project_name: diary.project_name,
    diary_date: diary.diary_date,
  };
  for (const k of [
    "project_code", "client_name", "site_description", "rail_week",
    "shift_start", "shift_end", "contract_po_number",
  ] as const) {
    const v = diary[k];
    if (v && String(v).trim()) payload[k] = String(v).trim();
  }
  await pushEvents([
    {
      aggregate_type: "site_diary",
      aggregate_id: diaryId,
      event_type: "SiteDiaryCreated",
      payload,
    },
  ]);
  return diaryId;
}

/** Attach a marked-up drawing (snapshot) to a diary as visual evidence. */
export const attachDiaryDrawing = (
  diaryId: string,
  drawing: {
    title: string;
    file_id: string;
    markup?: Record<string, unknown> | null;
    source_work_pack_id?: string;
    source_document_id?: string;
    source_drawing_id?: string;
    page_number?: number;
  },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "site_diary",
      aggregate_id: diaryId,
      event_type: "SiteDiaryDrawingAttached",
      payload: {
        drawing_id: crypto.randomUUID(),
        attached_at: new Date().toISOString(),
        ...drawing,
      },
    },
  ]);

export const removeDiaryDrawing = (
  diaryId: string,
  drawingId: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "site_diary",
      aggregate_id: diaryId,
      event_type: "SiteDiaryDrawingRemoved",
      payload: { drawing_id: drawingId },
    },
  ]);

/** Attach an already-uploaded photo to a diary (audit record; file is in S3). */
export const attachDiaryPhoto = (
  diaryId: string,
  photo: {
    field_path: string;
    file_id: string;
    sha256: string;
    mime_type: string;
    size_bytes?: number;
  },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "site_diary",
      aggregate_id: diaryId,
      event_type: "SiteDiaryPhotoAttached",
      payload: { ...photo },
    },
  ]);

export const detachDiaryPhoto = (
  diaryId: string,
  photo: { field_path: string; file_id: string },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "site_diary",
      aggregate_id: diaryId,
      event_type: "SiteDiaryPhotoDetached",
      payload: { ...photo },
    },
  ]);

export const getSiteDiaryHistory = (
  id: string,
): Promise<DiaryHistoryEntry[]> => apiGet(`/site-diaries/${id}/history`);

// ---------- Drawings (plans) ----------

export interface DrawingUpload {
  upload_id: string;
  position: number;
  file_id: string;
  original_filename: string | null;
  mime_type: string;
  page_count: number;
}
export interface DrawingRevision {
  revision_id: string;
  label: string;
  issue_date: string | null;
  notes: string | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
  uploads: DrawingUpload[];
}
export interface DrawingPin {
  pin_id: string;
  page_number: number;
  x: number;
  y: number;
  label: string | null;
  defect_id: string | null;
  created_by_name: string | null;
  created_at: string | null;
}
export interface DrawingRow {
  id: string;
  project_id: string;
  project_name: string;
  name: string;
  folder_name: string | null;
  revisions: DrawingRevision[];
  current_revision_id: string | null;
  page_count: number;
  annotations: Record<string, unknown[]>;
  pins: DrawingPin[];
  created_at: string;
  created_by_name: string | null;
  last_updated_at: string | null;
  last_updated_by_name: string | null;
}

export const listDrawings = (
  projectId?: string | null,
): Promise<DrawingRow[]> => apiGet(`/drawings${projectQuery(projectId)}`);

export const getDrawing = (id: string): Promise<DrawingRow> =>
  apiGet(`/drawings/${id}`);

/** Create a drawing with its "Initial" revision (set current). Returns the ids. */
export async function createDrawing(input: {
  projectId: string;
  projectName: string;
  name: string;
  folderName?: string | null;
}): Promise<{ drawingId: string; revisionId: string }> {
  const drawingId = crypto.randomUUID();
  const revisionId = crypto.randomUUID();
  await pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: drawingId,
      event_type: "DrawingCreated",
      payload: {
        project_id: input.projectId,
        project_name: input.projectName,
        name: input.name,
        ...(input.folderName ? { folder_name: input.folderName } : {}),
      },
    },
    {
      aggregate_type: "drawing",
      aggregate_id: drawingId,
      event_type: "DrawingRevisionAdded",
      payload: { revision_id: revisionId, label: "Initial" },
    },
    {
      aggregate_type: "drawing",
      aggregate_id: drawingId,
      event_type: "DrawingCurrentRevisionSet",
      payload: { revision_id: revisionId },
    },
  ]);
  return { drawingId, revisionId };
}

export const renameDrawing = (id: string, name: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingRenamed",
      payload: { name },
    },
  ]);

/** Add a new revision (a re-issue). Optionally make it the current revision.
 *  Returns the new revision id so the caller can attach its uploads. */
export async function addDrawingRevision(
  id: string,
  input: { label: string; issueDate?: string; notes?: string; setCurrent?: boolean },
): Promise<string> {
  const revisionId = crypto.randomUUID();
  const events: NewEvent[] = [
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingRevisionAdded",
      payload: {
        revision_id: revisionId,
        label: input.label,
        ...(input.issueDate ? { issue_date: input.issueDate } : {}),
        ...(input.notes ? { notes: input.notes } : {}),
      },
    },
  ];
  if (input.setCurrent) {
    events.push({
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingCurrentRevisionSet",
      payload: { revision_id: revisionId },
    });
  }
  await pushEvents(events);
  return revisionId;
}

export const setDrawingCurrentRevision = (
  id: string,
  revisionId: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingCurrentRevisionSet",
      payload: { revision_id: revisionId },
    },
  ]);

export const setDrawingFolder = (
  id: string,
  folderName: string | null,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingFolderSet",
      payload: { folder_name: folderName },
    },
  ]);

export const addDrawingUpload = (
  id: string,
  upload: {
    revisionId: string;
    position: number;
    fileId: string;
    originalFilename?: string;
    mimeType: string;
    pageCount: number;
  },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingUploadAdded",
      payload: {
        upload_id: crypto.randomUUID(),
        revision_id: upload.revisionId,
        position: upload.position,
        file_id: upload.fileId,
        mime_type: upload.mimeType,
        page_count: upload.pageCount,
        ...(upload.originalFilename
          ? { original_filename: upload.originalFilename }
          : {}),
      },
    },
  ]);

export const removeDrawingUpload = (
  id: string,
  uploadId: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingUploadRemoved",
      payload: { upload_id: uploadId },
    },
  ]);

/** Replace a page's markup with the given shapes (empty clears the page). */
export const markUpDrawingPage = (
  id: string,
  pageNumber: number,
  shapes: unknown[],
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingPageMarkedUp",
      payload: { page_number: pageNumber, shapes },
    },
  ]);

export const deleteDrawing = (id: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingDeleted",
      payload: {},
    },
  ]);

/** Drop a pin on a drawing page. Returns the new pin id. */
export async function dropDrawingPin(
  id: string,
  pin: {
    pageNumber: number;
    x: number;
    y: number;
    label?: string;
    defectId?: string;
  },
): Promise<string> {
  const pinId = crypto.randomUUID();
  await pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingPinDropped",
      payload: {
        pin_id: pinId,
        page_number: pin.pageNumber,
        x: pin.x,
        y: pin.y,
        ...(pin.label ? { label: pin.label } : {}),
        ...(pin.defectId ? { defect_id: pin.defectId } : {}),
      },
    },
  ]);
  return pinId;
}

export const moveDrawingPin = (
  id: string,
  pinId: string,
  x: number,
  y: number,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingPinMoved",
      payload: { pin_id: pinId, x, y },
    },
  ]);

export const updateDrawingPin = (
  id: string,
  pinId: string,
  patch: { label?: string; defectId?: string },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingPinUpdated",
      payload: {
        pin_id: pinId,
        ...(patch.label !== undefined ? { label: patch.label } : {}),
        ...(patch.defectId ? { defect_id: patch.defectId } : {}),
      },
    },
  ]);

export const removeDrawingPin = (id: string, pinId: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "drawing",
      aggregate_id: id,
      event_type: "DrawingPinRemoved",
      payload: { pin_id: pinId },
    },
  ]);

export const getSiteDiary = (id: string): Promise<SiteDiaryDetail> =>
  apiGet(`/site-diaries/${id}`);

/** Client formally accepts an approved diary (brief §1.5). */
export const acceptSiteDiary = (
  id: string,
  signatureData: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "site_diary",
      aggregate_id: id,
      event_type: "SiteDiaryClientAccepted",
      payload: {
        accepted_at: new Date().toISOString(),
        signature_data: signatureData,
      },
    },
  ]);

/** Generate (or regenerate) the §1.4 works-completed summary for a diary —
 *  the day's signed-off check sheets, grouped by location + status. */
export const generateDiaryWorksSummary = (
  id: string,
): Promise<{
  summary: string;
  model: string | null;
  generated_at: string;
  source_sheet_count: number;
}> => apiPost(`/site-diaries/${id}/works-summary`, {});

/** Draft the diary's 'work completed this shift' narrative (Phase 2) from the
 *  day's records — signed-off check sheets, defects/close calls raised on the
 *  diary, freeform notes, planned work, plant. Returns the draft to review +
 *  edit (writes no event). 409 when there's nothing recorded yet to draft from. */
export const draftDiaryReport = (
  id: string,
): Promise<{
  draft: string;
  model: string | null;
  check_sheet_count: number;
  defect_count: number;
  observation_count: number;
  entry_count: number;
}> => apiPost(`/site-diaries/${id}/draft-report`, {});

export interface DiaryReviewFinding {
  severity: "warning" | "suggestion";
  message: string;
  field_path: string | null;
}

/** Pre-sign-off sense check (Phase 4): flags inconsistencies, missing-but-
 *  implied items and gaps. Advisory — writes no event, never blocks sign-off. */
export const reviewDiary = (
  id: string,
): Promise<{
  ok: boolean;
  findings: DiaryReviewFinding[];
  model: string | null;
}> => apiPost(`/site-diaries/${id}/review`, {});

export const listStaffBriefings = (
  projectId?: string | null,
): Promise<StaffBriefingSummary[]> =>
  apiGet(`/staff-briefings${projectQuery(projectId)}`);

/** Briefings raised from a given work pack (its site briefing / sign-on). */
export const listBriefingsForPack = (
  workPackId: string,
): Promise<StaffBriefingSummary[]> =>
  apiGet(`/staff-briefings?work_pack_id=${workPackId}`);

export const getStaffBriefing = (id: string): Promise<StaffBriefingDetail> =>
  apiGet(`/staff-briefings/${id}`);

export const getStaffBriefingHistory = (
  id: string,
): Promise<BriefingHistoryEntry[]> =>
  apiGet(`/staff-briefings/${id}/history`);

/** Fetch the Briefing & Sign-on Register PDF as a blob (auth-gated). */
export async function fetchStaffBriefingPdf(id: string): Promise<Blob> {
  const response = await apiFetch(`${env.apiBaseUrl}/staff-briefings/${id}/pdf`, {
    headers: { authorization: `Bearer ${await tokenProvider()}` },
  });
  if (!response.ok) throw new Error(`PDF returned ${response.status}`);
  return response.blob();
}

/**
 * Raise a Staff Briefing (sign-on register) from a work pack, pre-populated
 * from the pack's Task Brief. Returns the new briefing id. The gang then
 * signs on against it (mobile/on-site) — we don't re-implement sign-off.
 */
/** Briefings raised against a given site diary (its sign-on register). */
export const listBriefingsForDiary = (
  diaryId: string,
): Promise<StaffBriefingSummary[]> =>
  apiGet(`/staff-briefings?diary_id=${diaryId}`);

/**
 * Create a Staff Briefing (sign-on register). Returns the new id. Used by the
 * contextual launchers (from a diary / ad-hoc) — the work-pack launcher is
 * createBriefingFromPack.
 */
export async function createStaffBriefing(briefing: {
  project_id: string;
  project_name: string;
  project_code?: string;
  briefing_date: string;
  rail_week?: string;
  task_description?: string;
  briefer_name: string;
  diary_id?: string;
}): Promise<string> {
  const briefingId = crypto.randomUUID();
  const payload: Record<string, unknown> = {
    project_id: briefing.project_id,
    project_name: briefing.project_name,
    briefing_date: briefing.briefing_date,
    briefer: { name: briefing.briefer_name.trim() || "Site supervisor" },
  };
  if (briefing.project_code) payload.project_code = briefing.project_code;
  if (briefing.rail_week) payload.rail_week = briefing.rail_week;
  if (briefing.task_description) payload.task_description = briefing.task_description;
  if (briefing.diary_id) payload.diary_id = briefing.diary_id;
  await pushEvents([
    {
      aggregate_type: "staff_briefing",
      aggregate_id: briefingId,
      event_type: "BriefingCreated",
      payload,
    },
  ]);
  return briefingId;
}

export async function createBriefingFromPack(
  pack: WorkPackRow,
): Promise<string> {
  const brief = pack.task_brief ?? {};
  const briefingId = crypto.randomUUID();
  await pushEvents([
    {
      aggregate_type: "staff_briefing",
      aggregate_id: briefingId,
      event_type: "BriefingCreated",
      payload: {
        project_id: pack.project_id,
        project_name: pack.project_name,
        briefing_date: pack.shift_date ?? new Date().toISOString().slice(0, 10),
        task_description: brief.description || undefined,
        // Snapshot the full task brief so the record shows what was briefed.
        ...(pack.task_brief ? { briefed_content: pack.task_brief } : {}),
        briefer: { name: (brief.supervisor || "").trim() || "Site supervisor" },
        work_pack_id: pack.id,
      },
    },
  ]);
  return briefingId;
}

/**
 * Edit the structured "what was briefed" content of an existing briefing —
 * used on ad-hoc briefings that weren't raised from a work pack. Keeps the
 * header one-line task description in step with the content's description.
 */
export const updateBriefingContent = (
  briefingId: string,
  content: WorkPackTaskBrief,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "staff_briefing",
      aggregate_id: briefingId,
      event_type: "BriefingContentUpdated",
      payload: {
        briefed_content: content,
        ...(content.description ? { task_description: content.description } : {}),
      },
    },
  ]);

/** Synthesise a spoken safety briefing (Phase 2) from the structured RAMS/task
 *  content. Returns the draft to review + edit into the notes; writes no event. */
export const draftBriefing = (
  briefedContent: WorkPackTaskBrief,
): Promise<{ draft: string; model: string | null }> =>
  apiPost(`/staff-briefings/draft-briefing`, {
    briefed_content: briefedContent,
  });

export const listDefects = (
  projectId?: string | null,
  status?: string,
  snagsOnly?: boolean,
): Promise<DefectRow[]> => {
  const params = new URLSearchParams();
  if (projectId) params.set("project_id", projectId);
  if (status) params.set("status", status);
  if (snagsOnly) params.set("snags_only", "true");
  const qs = params.toString();
  return apiGet(`/defects${qs ? `?${qs}` : ""}`);
};

// ---------- Project handover / close-out ----------

export interface ProjectHandover {
  project_id: string;
  signed_off: boolean;
  signed_off_at: string | null;
  signed_off_by_name: string | null;
  note: string | null;
  open_snags: number;
  open_defects: number;
  practical_completion_date: string | null;
  dlp_months: number | null;
  dlp_ends_on: string | null;
  open_dlp_defects: number;
  final_certificate_issued: boolean;
  final_certificate_at: string | null;
  final_certificate_by_name: string | null;
  documents: HandoverDocument[];
}

export interface HandoverDocument {
  document_id: string;
  kind: string;
  title: string;
  file_id: string;
  mime_type: string | null;
  attached_at: string;
  attached_by_name: string | null;
}

export const getProjectHandover = (
  projectId: string,
): Promise<ProjectHandover> => apiGet(`/projects/${projectId}/handover`);

export interface HandoverHistoryEntry {
  seq: number;
  event_type: string;
  label: string;
  actor_name: string | null;
  at: string;
}

export const getHandoverHistory = (
  projectId: string,
): Promise<HandoverHistoryEntry[]> =>
  apiGet(`/projects/${projectId}/handover/history`);

/** The Certificate of Practical Completion / Final Certificate PDF (blob). */
export async function fetchHandoverCertificate(
  projectId: string,
  kind: "practical_completion" | "final",
): Promise<Blob> {
  const response = await apiFetch(
    `${env.apiBaseUrl}/projects/${projectId}/handover/certificate/${kind}`,
    { headers: { authorization: `Bearer ${await tokenProvider()}` } },
  );
  if (!response.ok) {
    throw new Error(`Certificate returned ${response.status}`);
  }
  return response.blob();
}

/** Accept the project at practical completion (close-out sign-off), starting
 * the Defects Liability Period if a completion date + length are given. */
export const signOffHandover = (
  projectId: string,
  opts: { note?: string; practicalCompletionDate?: string; dlpMonths?: number } = {},
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectHandoverSignedOff",
      payload: {
        signed_off_at: new Date().toISOString(),
        ...(opts.note ? { note: opts.note } : {}),
        ...(opts.practicalCompletionDate
          ? { practical_completion_date: opts.practicalCompletionDate }
          : {}),
        ...(opts.dlpMonths != null ? { dlp_months: opts.dlpMonths } : {}),
      },
    },
  ]);

/** Attach a turnover document (O&M, as-built, warranty, compliance cert). */
export const attachHandoverDocument = (
  projectId: string,
  doc: { kind: string; title: string; fileId: string; mimeType?: string },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectHandoverDocumentAttached",
      payload: {
        document_id: crypto.randomUUID(),
        kind: doc.kind,
        title: doc.title,
        file_id: doc.fileId,
        attached_at: new Date().toISOString(),
        ...(doc.mimeType ? { mime_type: doc.mimeType } : {}),
      },
    },
  ]);

export const removeHandoverDocument = (
  projectId: string,
  documentId: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectHandoverDocumentRemoved",
      payload: { document_id: documentId },
    },
  ]);

/** Issue the final certificate at the end of the DLP (final acceptance). */
export const issueFinalCertificate = (
  projectId: string,
  note?: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectFinalCertificateIssued",
      payload: {
        issued_at: new Date().toISOString(),
        ...(note ? { note } : {}),
      },
    },
  ]);

/** Reopen a handover (work found outstanding after sign-off). */
export const reopenHandover = (
  projectId: string,
  reason?: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "project",
      aggregate_id: projectId,
      event_type: "ProjectHandoverReopened",
      payload: { ...(reason ? { reason } : {}) },
    },
  ]);

/**
 * Office fallback: raise a defect from the web (normally raised on site from
 * the diary, or inside an ITP). Emits DefectRaised scoped to the project.
 * Priority / target date / assignee are set afterwards on the detail page.
 * Returns the new defect id.
 */
export async function raiseDefect(input: {
  projectId: string;
  description: string;
  category: string;
  riskRating?: string;
  locationRef?: string;
  structureId?: string;
  isSnag?: boolean;
  issueType?: string;
  trade?: string;
}): Promise<string> {
  const id = crypto.randomUUID();
  await pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectRaised",
      payload: {
        description: input.description,
        category: input.category,
        project_id: input.projectId,
        raised_at: new Date().toISOString(),
        ...(input.riskRating ? { risk_rating: input.riskRating } : {}),
        ...(input.locationRef ? { location_ref: input.locationRef } : {}),
        ...(input.structureId ? { structure_id: input.structureId } : {}),
        ...(input.isSnag ? { is_snag: true } : {}),
        ...(input.issueType ? { issue_type: input.issueType } : {}),
        ...(input.trade ? { trade: input.trade } : {}),
      },
    },
  ]);
  return id;
}

export interface IssueRow {
  id: string;
  type: string;
  title: string;
  trade: string | null;
  location: string | null;
  assignee_name: string | null;
  priority: string | null;
  status: "open" | "in_progress" | "resolved";
  due: string | null;
  editable: boolean;
  route_name: string;
  route_id: string;
}

/** The unified Issues board for a project — defects + observations + sheets. */
export const getProjectIssues = (
  projectId: string,
): Promise<{ issues: IssueRow[] }> =>
  apiGet(`/projects/${projectId}/issues`);

export const getDefect = (id: string): Promise<DefectRow> =>
  apiGet(`/defects/${id}`);

export const getDefectHistory = (id: string): Promise<DefectHistoryEntry[]> =>
  apiGet(`/defects/${id}/history`);

/** Assign responsibility for a defect to a person. */
export const assignDefect = (
  id: string,
  assignedTo: string,
  assignedToName?: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectAssigned",
      payload: {
        assigned_to: assignedTo,
        ...(assignedToName ? { assigned_to_name: assignedToName } : {}),
      },
    },
  ]);

/** Amend a defect's fields (priority, target date, category, risk, snag, etc.). */
export const updateDefect = (
  id: string,
  fields: {
    description?: string;
    category?: string;
    risk_rating?: string;
    location_ref?: string;
    structure_id?: string;
    priority?: string;
    target_date?: string;
    is_snag?: boolean;
  },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectUpdated",
      payload: fields,
    },
  ]);

/** Move a defect through the register lifecycle. */
export const changeDefectStatus = (
  id: string,
  status: "open" | "in_progress" | "closed",
  note?: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectStatusChanged",
      payload: { status, ...(note ? { note } : {}) },
    },
  ]);

/** Close-out a defect with resolution notes (sets status to closed). */
export const resolveDefect = (
  id: string,
  resolutionNotes?: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectResolved",
      payload: {
        resolved_at: new Date().toISOString(),
        ...(resolutionNotes ? { resolution_notes: resolutionNotes } : {}),
      },
    },
  ]);

export interface RegisterReviewFinding {
  severity: "warning" | "suggestion";
  message: string;
}

/** AI triage of a defect description → suggested classification. A suggestion
 *  only; fields are null when no model is configured. */
export const triageDefect = (
  description: string,
): Promise<{
  category: string | null;
  risk_rating: string | null;
  priority: string | null;
  trade: string | null;
  model: string | null;
}> => apiPost(`/defects/triage`, { description });

export interface SimilarDefect {
  id: string;
  description: string;
  status: string;
  category: string | null;
  risk_rating: string | null;
  similarity: number;
}

/** Existing defects similar to a description being raised (duplicate check). */
export const similarDefects = (
  description: string,
  projectId?: string,
): Promise<{ matches: SimilarDefect[] }> =>
  apiPost(`/defects/similar`, {
    description,
    ...(projectId ? { project_id: projectId } : {}),
  });

/** Draft the resolution note (Phase 3) from the defect description + comments. */
export const draftDefectResolution = (
  id: string,
): Promise<{ draft: string; model: string | null }> =>
  apiPost(`/defects/${id}/draft-resolution`, {});

/** Pre-close completeness review of a defect (Phase 3). Advisory. */
export const reviewDefect = (
  id: string,
): Promise<{ ok: boolean; findings: RegisterReviewFinding[] }> =>
  apiPost(`/defects/${id}/review`, {});

/** Add a comment to an issue (interleaves into its activity feed). */
export const addDefectComment = (id: string, body: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectCommented",
      payload: {
        comment_id: crypto.randomUUID(),
        body,
        commented_at: new Date().toISOString(),
      },
    },
  ]);

/** Attach a file (already uploaded to S3) to an issue. */
export const addDefectAttachment = (
  id: string,
  file: { fileId: string; filename: string; mimeType?: string },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectAttachmentAdded",
      payload: {
        file_id: file.fileId,
        filename: file.filename,
        ...(file.mimeType ? { mime_type: file.mimeType } : {}),
      },
    },
  ]);

export const removeDefectAttachment = (
  id: string,
  fileId: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectAttachmentRemoved",
      payload: { file_id: fileId },
    },
  ]);

/** Watch an issue — emailed on status / comment / resolve. */
export const watchDefect = (
  id: string,
  watcher: { email?: string | null; name?: string | null },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectWatched",
      payload: {
        ...(watcher.email ? { email: watcher.email } : {}),
        ...(watcher.name ? { name: watcher.name } : {}),
      },
    },
  ]);

export const unwatchDefect = (id: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectUnwatched",
      payload: {},
    },
  ]);

// The inverse of each relation, recorded on the other issue so both sides
// show the link.
const _INVERSE_RELATION: Record<string, string> = {
  relates: "relates",
  blocks: "blocked_by",
  blocked_by: "blocks",
  duplicate: "duplicate",
};

/** Link two issues — records the relation on both sides. */
export const linkDefect = (
  id: string,
  relatedId: string,
  relation: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectLinked",
      payload: { related_id: relatedId, relation },
    },
    {
      aggregate_type: "defect",
      aggregate_id: relatedId,
      event_type: "DefectLinked",
      payload: { related_id: id, relation: _INVERSE_RELATION[relation] ?? "relates" },
    },
  ]);

/** Remove the link between two issues (both sides). */
export const unlinkDefect = (id: string, relatedId: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "defect",
      aggregate_id: id,
      event_type: "DefectUnlinked",
      payload: { related_id: relatedId },
    },
    {
      aggregate_type: "defect",
      aggregate_id: relatedId,
      event_type: "DefectUnlinked",
      payload: { related_id: id },
    },
  ]);

/** Fetch the issue PDF as a blob (auth-gated). */
export async function fetchDefectPdf(id: string): Promise<Blob> {
  const response = await apiFetch(`${env.apiBaseUrl}/defects/${id}/pdf`, {
    headers: { authorization: `Bearer ${await tokenProvider()}` },
  });
  if (!response.ok) throw new Error(`PDF returned ${response.status}`);
  return response.blob();
}

export const listObservations = (
  projectId?: string | null,
  kind?: string,
  status?: string,
): Promise<ObservationRow[]> => {
  const params = new URLSearchParams();
  if (projectId) params.set("project_id", projectId);
  if (kind) params.set("kind", kind);
  if (status) params.set("status", status);
  const qs = params.toString();
  return apiGet(`/observations${qs ? `?${qs}` : ""}`);
};

export const getObservation = (id: string): Promise<ObservationRow> =>
  apiGet(`/observations/${id}`);

export const getObservationHistory = (
  id: string,
): Promise<ObservationHistoryEntry[]> =>
  apiGet(`/observations/${id}/history`);

/** Office fallback: raise a close call / good practice from the web. */
export async function raiseObservation(input: {
  projectId: string;
  kind: string;
  description: string;
  observationType?: string;
  locationRef?: string;
}): Promise<string> {
  const id = crypto.randomUUID();
  await pushEvents([
    {
      aggregate_type: "observation",
      aggregate_id: id,
      event_type: "ObservationRaised",
      payload: {
        kind: input.kind,
        description: input.description,
        project_id: input.projectId,
        raised_at: new Date().toISOString(),
        ...(input.observationType ? { observation_type: input.observationType } : {}),
        ...(input.locationRef ? { location_ref: input.locationRef } : {}),
      },
    },
  ]);
  return id;
}

/** Amend an observation (incl. the action-taken / mitigation record). */
export const updateObservation = (
  id: string,
  fields: {
    description?: string;
    observation_type?: string;
    location_ref?: string;
    action_taken?: string;
  },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "observation",
      aggregate_id: id,
      event_type: "ObservationUpdated",
      payload: fields,
    },
  ]);

/** Suggest the corrective action to close the loop (Phase 3). */
export const draftObservationAction = (
  id: string,
): Promise<{ draft: string; model: string | null }> =>
  apiPost(`/observations/${id}/draft-action`, {});

/** Pre-close completeness review of an observation (Phase 3). Advisory. */
export const reviewObservation = (
  id: string,
): Promise<{ ok: boolean; findings: RegisterReviewFinding[] }> =>
  apiPost(`/observations/${id}/review`, {});

export const assignObservation = (
  id: string,
  assignedTo: string,
  assignedToName?: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "observation",
      aggregate_id: id,
      event_type: "ObservationAssigned",
      payload: {
        assigned_to: assignedTo,
        ...(assignedToName ? { assigned_to_name: assignedToName } : {}),
      },
    },
  ]);

export const changeObservationStatus = (
  id: string,
  status: "open" | "in_progress" | "closed",
  note?: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "observation",
      aggregate_id: id,
      event_type: "ObservationStatusChanged",
      payload: { status, ...(note ? { note } : {}) },
    },
  ]);

// ---------- Work packs (brief §1.1) ----------

export interface WorkPackDocument {
  document_id: string;
  kind: string;
  title: string;
  file_id: string | null;
  url: string | null;
  location_ref?: string | null;
  attached_at: string;
  // Present once the drawing has been marked up on the mobile app.
  markup?: Record<string, unknown> | null;
  marked_up_at?: string | null;
  marked_up_by_name?: string | null;
}

export interface WorkPackBomItem {
  item_id: string;
  description: string;
  quantity: number | null;
  unit: string | null;
  notes: string | null;
  added_at: string;
}

export interface TaskBriefHazard { hazard: string; control: string }
export interface TaskBriefPlant { item: string; tag: string }
export interface TaskBriefPermit { type: string; reference: string }

export interface WorkPackTaskBrief {
  description?: string;
  location?: string;
  supervisor?: string;
  hazards?: TaskBriefHazard[];
  ppe?: string[];
  plant?: TaskBriefPlant[];
  permits?: TaskBriefPermit[];
  notes?: string;
}

/** The higher-level construction / project brief (planning), distinct from the
 *  pre-shift safety task brief. All free-text, grouped into four sections. */
export interface WorkPackConstructionBrief {
  // 1 · Overview & objectives
  strategic_purpose?: string;
  success_criteria?: string;
  // 2 · Scope & boundaries
  included_works?: string;
  exclusions?: string;
  site_information?: string;
  // 3 · Requirements & constraints
  functional_requirements?: string;
  technical_specs?: string;
  budget?: string;
  milestones?: string;
  // 4 · Risks & governance
  key_risks?: string;
  assumptions?: string;
  procurement_strategy?: string;
  change_control?: string;
}

/** Where on the railway the work is (NR reference + plottable coordinates). */
export interface WorkPackLocation {
  elr?: string | null;
  mileage_from?: string | null;
  mileage_to?: string | null;
  line_id?: string | null;
  track_id?: string | null;
  access_point?: string | null;
  location_name?: string | null;
  postcode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  notes?: string | null;
}

export interface WorkPackRoleHolder {
  name?: string | null;
  person_id?: string | null;
}

/** The NR/L2/OHS/019 roles on a pack. */
export interface WorkPackRoles {
  planner?: WorkPackRoleHolder;
  responsible_manager?: WorkPackRoleHolder;
  person_in_charge?: WorkPackRoleHolder;
  coss?: WorkPackRoleHolder;
}
export type WorkPackRoleKey = keyof WorkPackRoles;

export interface WorkPackReview {
  section: string;
  stage: "verify" | "authorise";
  revision: number;
  actor_id: string | null;
  actor_name: string | null;
  reviewed_at: string;
}

/** Pack lifecycle: draft -> verified (PIC) -> authorised (RM) -> issued. */
export type WorkPackStatus = "draft" | "verified" | "authorised" | "issued";

export const WORK_PACK_STATUS_LABEL: Record<WorkPackStatus, string> = {
  draft: "Draft",
  verified: "Verified",
  authorised: "Authorised",
  issued: "Issued",
};

export interface WorkPackRow {
  id: string;
  project_id: string;
  project_name: string;
  reference: string | null;
  title: string;
  task_type: string | null;
  status: WorkPackStatus | string;
  revision: number;
  location: WorkPackLocation | null;
  roles: WorkPackRoles | null;
  reviews: WorkPackReview[];
  verified_at: string | null;
  verified_by_name: string | null;
  authorised_at: string | null;
  authorised_by_name: string | null;
  documents: WorkPackDocument[];
  bom: WorkPackBomItem[];
  task_brief: WorkPackTaskBrief | null;
  construction_brief: WorkPackConstructionBrief | null;
  issued_to: string[];
  issued_at: string | null;
  issued_by_name: string | null;
  shift_date: string | null;
  created_at: string;
  created_by_name: string | null;
  last_updated_at: string | null;
  last_updated_by_name: string | null;
}

export interface WorkPackHistoryEntry {
  seq: number;
  event_type: string;
  label: string;
  actor_name: string | null;
  at: string;
}

export interface WorkPackTemplate {
  task_type: string;
  items: Array<{ kind: string; label: string }>;
  brief: WorkPackTaskBrief | null;
}

/** The documents a pack should contain, per task type (seed). */
export const listWorkPackTemplates = (): Promise<WorkPackTemplate[]> =>
  apiGet("/work-packs/templates");

export const listWorkPacks = (
  projectId?: string | null,
): Promise<WorkPackRow[]> =>
  apiGet(`/work-packs${projectQuery(projectId)}`);

export const getWorkPack = (id: string): Promise<WorkPackRow> =>
  apiGet(`/work-packs/${id}`);

export const getWorkPackHistory = (
  id: string,
): Promise<WorkPackHistoryEntry[]> => apiGet(`/work-packs/${id}/history`);

// ---- Pack control (NR/L2/OHS/019) ----

export interface WorkPackControlSection {
  key: string;
  label: string;
  reviewed_by_me: boolean;
  reviewed_at: string | null;
  reviewed_by_name: string | null;
}

/** What the pack-control panel needs, computed server-side for the caller:
 *  the stage, the key sections they must open before signing, what's still
 *  missing, and whether they may verify / authorise / issue right now. */
export interface WorkPackControl {
  status: WorkPackStatus | string;
  stage: "verify" | "authorise" | "issue" | "done";
  revision: number;
  sections: WorkPackControlSection[];
  missing: string[];
  can_verify: boolean;
  can_authorise: boolean;
  can_issue: boolean;
  blockers: string[];
  reviews: WorkPackReview[];
}

export const getWorkPackControl = (id: string): Promise<WorkPackControl> =>
  apiGet(`/work-packs/${id}/control`);

export const updateWorkPackLocation = (
  packId: string,
  location: WorkPackLocation,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackLocationUpdated",
      payload: { location, updated_at: new Date().toISOString() },
    },
  ]);

export const updateWorkPackRoles = (
  packId: string,
  roles: WorkPackRoles,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackRolesUpdated",
      payload: { roles, updated_at: new Date().toISOString() },
    },
  ]);

/** Record that the caller opened and reviewed a key section at this stage. */
export const reviewWorkPackSection = (
  packId: string,
  section: string,
  stage: "verify" | "authorise",
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackSectionReviewed",
      payload: { section, stage, reviewed_at: new Date().toISOString() },
    },
  ]);

export const verifyWorkPack = (packId: string, note?: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackVerified",
      payload: { verified_at: new Date().toISOString(), ...(note ? { note } : {}) },
    },
  ]);

export const authoriseWorkPack = (packId: string, note?: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackAuthorised",
      payload: {
        authorised_at: new Date().toISOString(),
        ...(note ? { note } : {}),
      },
    },
  ]);

export const issueWorkPack = (packId: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackIssued",
      payload: { issued_at: new Date().toISOString() },
    },
  ]);

/** Add a line item to a pack's structured Bill of Materials. */
export const addBomItem = (
  packId: string,
  line: { description: string; quantity?: number | null; unit?: string | null; notes?: string | null },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackBomItemAdded",
      payload: {
        item_id: crypto.randomUUID(),
        description: line.description,
        quantity: line.quantity ?? null,
        unit: line.unit || null,
        notes: line.notes || null,
        added_at: new Date().toISOString(),
      },
    },
  ]);

export const removeBomItem = (
  packId: string,
  itemId: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackBomItemRemoved",
      payload: { item_id: itemId, removed_at: new Date().toISOString() },
    },
  ]);

/** Soft-delete a work pack — hidden from lists, history kept, reversible. */
export const deleteWorkPack = (
  packId: string,
  reason?: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackDeleted",
      payload: reason ? { reason } : {},
    },
  ]);

/** Save the structured Task Brief (whole object — a form saved as one). */
export const updateTaskBrief = (
  packId: string,
  brief: WorkPackTaskBrief,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackTaskBriefUpdated",
      payload: { brief, updated_at: new Date().toISOString() },
    },
  ]);

/** Save the construction / project brief (whole object — saved as one form). */
export const updateConstructionBrief = (
  packId: string,
  brief: WorkPackConstructionBrief,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "work_pack",
      aggregate_id: packId,
      event_type: "WorkPackConstructionBriefUpdated",
      payload: { brief, updated_at: new Date().toISOString() },
    },
  ]);

export type BriefSection = "overview" | "scope" | "requirements" | "risks";

export interface BriefSuggestResult {
  suggestions: Record<string, string>;
  model: string | null;
}

/** AI draft for one brief section (grounded in project context + past records).
 *  Throws with the API's message (e.g. "not configured") on failure. */
export async function suggestConstructionBrief(
  packId: string,
  section: BriefSection,
): Promise<BriefSuggestResult> {
  const response = await apiFetch(
    `${env.apiBaseUrl}/work-packs/${packId}/brief/suggest`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${await tokenProvider()}`,
      },
      body: JSON.stringify({ section }),
    },
  );
  if (!response.ok) {
    throw await detailedError(response, "/work-packs/brief/suggest");
  }
  return response.json() as Promise<BriefSuggestResult>;
}

/** Structure a spoken/typed job description into the brief fields (all
 *  sections). Capture (dictation/typing) is the caller's concern. */
export async function structureConstructionBrief(
  packId: string,
  transcript: string,
): Promise<BriefSuggestResult> {
  const response = await apiFetch(
    `${env.apiBaseUrl}/work-packs/${packId}/brief/structure`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${await tokenProvider()}`,
      },
      body: JSON.stringify({ transcript }),
    },
  );
  if (!response.ok) {
    throw await detailedError(response, "/work-packs/brief/structure");
  }
  return response.json() as Promise<BriefSuggestResult>;
}

export interface BriefImproveResult {
  text: string;
  model: string | null;
}

/** Rewrite the free-text job description into clearer, professional prose.
 *  Tidies the description only — does not touch the structured brief fields. */
export async function improveConstructionDescription(
  packId: string,
  text: string,
): Promise<BriefImproveResult> {
  const response = await apiFetch(
    `${env.apiBaseUrl}/work-packs/${packId}/brief/improve-description`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${await tokenProvider()}`,
      },
      body: JSON.stringify({ text }),
    },
  );
  if (!response.ok) {
    throw await detailedError(response, "/work-packs/brief/improve-description");
  }
  return response.json() as Promise<BriefImproveResult>;
}

/** Read an attached document (text PDF / text) and draft the brief from it. */
export async function briefFromDocument(
  packId: string,
  documentId: string,
): Promise<BriefSuggestResult> {
  const response = await apiFetch(
    `${env.apiBaseUrl}/work-packs/${packId}/brief/from-document`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${await tokenProvider()}`,
      },
      body: JSON.stringify({ document_id: documentId }),
    },
  );
  if (!response.ok) {
    throw await detailedError(response, "/work-packs/brief/from-document");
  }
  return response.json() as Promise<BriefSuggestResult>;
}

// ---------- Event push (write path) ----------
//
// The office web app is online-only, so it writes by POSTing events
// straight to the sync endpoint the mobile app also uses — one ingest
// path for the whole platform.

// ---------- Quality check sheets (Module 1.4) ----------

export interface CheckSheetSummary {
  id: string;
  project_id: string;
  structure_id: string;
  template_id: string;
  template_version: string;
  inspection_type: string;
  status: string;
  defect_count: number;
  photo_count: number;
  created_at: string;
  signed_off_at: string | null;
  approved_at: string | null;
  last_updated_at: string;
  ai_summary: string | null;
}

export interface CheckSheetDetail extends CheckSheetSummary {
  answers: Record<string, unknown>;
  drawings: Array<Record<string, unknown>>;
  // Photos captured against the sheet's photo fields (mobile, in field).
  photos: Array<Record<string, unknown>>;
  created_by: string;
  created_by_name: string | null;
  signed_off_by: string | null;
  signed_off_by_name: string | null;
  signed_off_signature: string | null;
  signed_off_gps: Record<string, number> | null;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_signature: string | null;
  client_accepted_at: string | null;
  client_accepted_by_name: string | null;
  client_accepted_signature: string | null;
  last_updated_by_name: string | null;
  ai_summary_at: string | null;
  ai_summary_model: string | null;
}

/** Attach a marked-up drawing (snapshot) to a check sheet as evidence. */
export const attachCheckSheetDrawing = (
  checkSheetId: string,
  drawing: {
    title: string;
    file_id: string;
    markup?: Record<string, unknown> | null;
    source_work_pack_id?: string;
    source_document_id?: string;
    source_drawing_id?: string;
    page_number?: number;
  },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "quality_check_sheet",
      aggregate_id: checkSheetId,
      event_type: "InspectionDrawingAttached",
      payload: {
        drawing_id: crypto.randomUUID(),
        title: drawing.title,
        file_id: drawing.file_id,
        markup: drawing.markup ?? undefined,
        source_work_pack_id: drawing.source_work_pack_id,
        source_document_id: drawing.source_document_id,
        source_drawing_id: drawing.source_drawing_id,
        page_number: drawing.page_number,
        attached_at: new Date().toISOString(),
      },
    },
  ]);

export const removeCheckSheetDrawing = (
  checkSheetId: string,
  drawingId: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "quality_check_sheet",
      aggregate_id: checkSheetId,
      event_type: "InspectionDrawingRemoved",
      payload: { drawing_id: drawingId },
    },
  ]);

/** Attach an uploaded photo to a check sheet (evidence). */
export const attachCheckSheetPhoto = (
  checkSheetId: string,
  photo: {
    field_path: string;
    file_id: string;
    sha256: string;
    mime_type: string;
    size_bytes?: number;
  },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "quality_check_sheet",
      aggregate_id: checkSheetId,
      event_type: "InspectionPhotoAttached",
      payload: { ...photo },
    },
  ]);

export const detachCheckSheetPhoto = (
  checkSheetId: string,
  photo: { field_path: string; file_id: string },
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "quality_check_sheet",
      aggregate_id: checkSheetId,
      event_type: "InspectionPhotoDetached",
      payload: { ...photo },
    },
  ]);

export interface CheckSheetHistoryEntry {
  seq: number;
  event_type: string;
  label: string;
  actor_name: string | null;
  at: string;
}

export interface FormTemplateSummary {
  id: string;
  name: string;
  version: string;
  code: string | null;
  items: number;
}

export interface GridColumn {
  key: string;
  label: string;
  type: string;
}

/** A field in a form template (subset of the JSON-Schema property we render). */
export interface FormTemplateField {
  type?: string;
  title?: string;
  enum?: string[];
  "x-widget"?: string;
  "x-design"?: string | number;
  "x-unit"?: string;
  "x-item-no"?: string;
  "x-hold-point"?: boolean;
  "x-witness-point"?: boolean;
  "x-columns"?: GridColumn[];
}

export interface FormTemplate {
  id: string;
  name: string;
  version: string;
  schema: { properties: Record<string, FormTemplateField> };
  ui?: { groups?: Array<{ title?: string; fields: string[] }> };
}

export interface ParetoItem {
  label: string;
  count: number;
}

export interface QualityAnalytics {
  total: number;
  by_status: Record<string, number>;
  approved: number;
  reopened_ever: number;
  first_time_right: number;
  defect_total: number;
  top_nonconformances: ParetoItem[];
  nonconformances_by_template: ParetoItem[];
  defects_by_category: ParetoItem[];
  defects_by_severity: ParetoItem[];
  trend: Array<{ week: string; signed_off: number; nonconformances: number }>;
}

export const getQualityAnalytics = (
  projectId?: string | null,
): Promise<QualityAnalytics> =>
  apiGet(`/quality/analytics${projectQuery(projectId)}`);

export const listCheckSheets = (
  projectId?: string | null,
): Promise<CheckSheetSummary[]> =>
  apiGet(`/check-sheets${projectQuery(projectId)}`);

export const getCheckSheet = (id: string): Promise<CheckSheetDetail> =>
  apiGet(`/check-sheets/${id}`);

/**
 * Office fallback: create a check sheet from the web (normally created on
 * site via mobile). Emits InspectionCreated; the sheet starts in_progress
 * and is then completed on site. Returns the new check-sheet id.
 */
export async function createCheckSheet(input: {
  templateId: string;
  templateVersion: string;
  projectId: string;
  structureId: string;
  inspectionType: string;
}): Promise<string> {
  const id = crypto.randomUUID();
  await pushEvents([
    {
      aggregate_type: "quality_check_sheet",
      aggregate_id: id,
      event_type: "InspectionCreated",
      payload: {
        template_id: input.templateId,
        template_version: input.templateVersion,
        project_id: input.projectId,
        structure_id: input.structureId,
        inspection_type: input.inspectionType,
      },
    },
  ]);
  return id;
}

export const getCheckSheetHistory = (
  id: string,
): Promise<CheckSheetHistoryEntry[]> => apiGet(`/check-sheets/${id}/history`);

/** Generate (or regenerate) the AI summary of a check sheet. */
export const generateCheckSheetSummary = (
  id: string,
): Promise<{ summary: string; model: string | null; generated_at: string }> =>
  apiPost(`/check-sheets/${id}/ai-summary`, {});

export const listFormTemplates = (): Promise<FormTemplateSummary[]> =>
  apiGet("/form-templates");

export const getFormTemplate = (templateId: string): Promise<FormTemplate> =>
  apiGet(`/form-templates/${templateId}`);

/** OLE CRE approves a signed-off check sheet (locks the record). */
/** Inspector signs off a completed check sheet (office/desk equivalent of the
 *  in-field mobile sign-off). Signer defaults to the authenticated actor. */
export const signOffCheckSheet = (
  id: string,
  signatureData: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "quality_check_sheet",
      aggregate_id: id,
      event_type: "InspectionSignedOff",
      payload: {
        signed_at: new Date().toISOString(),
        signature_data: signatureData,
      },
    },
  ]);

export const approveCheckSheet = (
  id: string,
  signatureData: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "quality_check_sheet",
      aggregate_id: id,
      event_type: "InspectionApproved",
      payload: {
        approved_at: new Date().toISOString(),
        approver_role: "OLE CRE",
        signature_data: signatureData,
      },
    },
  ]);

/** Client formally accepts an approved check sheet (brief §1.5). */
export const acceptCheckSheet = (
  id: string,
  signatureData: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "quality_check_sheet",
      aggregate_id: id,
      event_type: "InspectionClientAccepted",
      payload: {
        accepted_at: new Date().toISOString(),
        signature_data: signatureData,
      },
    },
  ]);

/** Send a check sheet back for correction. */
export const reopenCheckSheet = (id: string, reason: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "quality_check_sheet",
      aggregate_id: id,
      event_type: "InspectionReopened",
      payload: { reason },
    },
  ]);

/** Fetch the combined submission record (ZIP: diary + linked check sheets). */
export async function fetchSiteDiaryCombined(id: string): Promise<Blob> {
  const response = await apiFetch(
    `${env.apiBaseUrl}/site-diaries/${id}/combined`,
    { headers: { authorization: `Bearer ${await tokenProvider()}` } },
  );
  if (!response.ok) {
    throw new Error(`Combined record returned ${response.status}`);
  }
  return response.blob();
}

/** Fetch the completed check-sheet PDF as a blob (the endpoint is auth-gated). */
export async function fetchCheckSheetPdf(id: string): Promise<Blob> {
  const response = await apiFetch(`${env.apiBaseUrl}/check-sheets/${id}/pdf`, {
    headers: { authorization: `Bearer ${await tokenProvider()}` },
  });
  if (!response.ok) {
    throw new Error(`PDF returned ${response.status}`);
  }
  return response.blob();
}

/** Branded, server-rendered Safety insights PDF for a project. */
export async function fetchSafetyInsightsPdf(projectId: string): Promise<Blob> {
  const response = await apiFetch(
    `${env.apiBaseUrl}/observations/safety-insights.pdf?project_id=${projectId}`,
    { headers: { authorization: `Bearer ${await tokenProvider()}` } },
  );
  if (!response.ok) {
    throw new Error(`PDF returned ${response.status}`);
  }
  return response.blob();
}

export interface NewEvent {
  aggregate_type: string;
  aggregate_id: string;
  event_type: string;
  payload: Record<string, unknown>;
}

interface RejectedEvent {
  reason: string;
  details: Record<string, unknown> | null;
}

/** Push a batch of client-originated events to the event log. */
export async function pushEvents(events: NewEvent[]): Promise<void> {
  const clientId = crypto.randomUUID();
  const occurredAt = new Date().toISOString();
  const body = {
    events: events.map((e, i) => ({
      client_id: clientId,
      client_seq: i + 1,
      aggregate_type: e.aggregate_type,
      aggregate_id: e.aggregate_id,
      event_type: e.event_type,
      payload: e.payload,
      occurred_at: occurredAt,
    })),
  };
  const response = await apiFetch(`${env.apiBaseUrl}/sync/events`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${await tokenProvider()}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`/sync/events returned ${response.status}`);
  }
  const result = (await response.json()) as { rejected: RejectedEvent[] };
  if (result.rejected?.length) {
    const first = result.rejected[0];
    const message = first.details?.message;
    throw new Error(
      typeof message === "string" && message
        ? message
        : `Event rejected (${first.reason})`,
    );
  }
}

// ---------- Weekly project reports ----------

export interface WeeklyReportSummary {
  id: string;
  project_id: string;
  project_name: string;
  week_label: string;
  period_start: string;
  period_end: string;
  prepared_by: string | null;
  status: string;
  rag: Record<string, string>;
  created_at: string;
}

export interface WeeklyRollup {
  shift_count: number;
  diaries: Array<{ id: string; diary_date: string; status: string }>;
  works_completed: string[];
  defects_total: number;
  defects_open: number;
  close_calls: number;
  good_practice: number;
  briefings: number;
  checks_completed: number;
  photos: number;
}

export interface WeeklyReportDetail extends WeeklyReportSummary {
  sections: Record<string, string>;
  rollup: WeeklyRollup;
  created_by_name: string | null;
  submitted_at: string | null;
  submitted_by_name: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  last_updated_at: string;
  last_updated_by_name: string | null;
}

export interface WeeklyReportHistoryEntry {
  seq: number;
  event_type: string;
  label: string;
  actor_name: string | null;
  at: string;
}

export const listWeeklyReports = (
  projectId?: string | null,
): Promise<WeeklyReportSummary[]> =>
  apiGet(`/weekly-reports${projectQuery(projectId)}`);

export const getWeeklyReport = (id: string): Promise<WeeklyReportDetail> =>
  apiGet(`/weekly-reports/${id}`);

export const getWeeklyReportHistory = (
  id: string,
): Promise<WeeklyReportHistoryEntry[]> =>
  apiGet(`/weekly-reports/${id}/history`);

/** Set one RAG dashboard dimension (scope/budget/timeline/quality). */
export const setWeeklyReportRag = (
  id: string,
  dimension: string,
  ragStatus: "red" | "amber" | "green",
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "weekly_report",
      aggregate_id: id,
      event_type: "WeeklyReportRagUpdated",
      payload: { dimension, status: ragStatus },
    },
  ]);

export const submitWeeklyReport = (id: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "weekly_report",
      aggregate_id: id,
      event_type: "WeeklyReportSubmitted",
      payload: { submitted_at: new Date().toISOString() },
    },
  ]);

/** Draft the four narrative sections (Phase 1) from this week's rolled-up data.
 *  Returns the drafts for the author to review + edit; writes no event. */
export const draftWeeklyReportNarrative = (
  id: string,
): Promise<{
  summary: string | null;
  roadblocks: string | null;
  look_ahead: string | null;
  resources: string | null;
  model: string | null;
}> => apiPost(`/weekly-reports/${id}/draft-narrative`, {});

export interface WeeklyReviewFinding {
  severity: "warning" | "suggestion";
  message: string;
}

/** Pre-submit review (Phase 2): completeness + narrative-vs-rollup
 *  consistency. Advisory — writes no event. */
export const reviewWeeklyReport = (
  id: string,
): Promise<{ ok: boolean; findings: WeeklyReviewFinding[] }> =>
  apiPost(`/weekly-reports/${id}/review`, {});

export interface RagSuggestion {
  status: "red" | "amber" | "green";
  rationale: string;
}

/** Suggest each RAG dimension's status + rationale from the week's data
 *  (Phase 3). Advisory — the author still sets each rating. */
export const suggestWeeklyReportRag = (
  id: string,
): Promise<{
  suggestions: Record<string, RagSuggestion>;
  model: string | null;
}> => apiPost(`/weekly-reports/${id}/suggest-rag`, {});

export const approveWeeklyReport = (id: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "weekly_report",
      aggregate_id: id,
      event_type: "WeeklyReportApproved",
      payload: { approved_at: new Date().toISOString() },
    },
  ]);

export const reopenWeeklyReport = (id: string, reason?: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "weekly_report",
      aggregate_id: id,
      event_type: "WeeklyReportReopened",
      payload: { ...(reason ? { reason } : {}) },
    },
  ]);

/** Fetch the weekly report PDF as a blob (the endpoint is auth-gated). */
export async function fetchWeeklyReportPdf(id: string): Promise<Blob> {
  const response = await apiFetch(
    `${env.apiBaseUrl}/weekly-reports/${id}/pdf`,
    { headers: { authorization: `Bearer ${await tokenProvider()}` } },
  );
  if (!response.ok) {
    throw new Error(`PDF returned ${response.status}`);
  }
  return response.blob();
}

// ---------- Workforce (Module 3) ----------

export interface WorkerRow {
  id: string;
  ncca_no: string;
  name: string;
  phone: string | null;
  email: string | null;
  // Auth0 sub of the linked app login (matches `me.id`), or null if the worker
  // has never signed into the app. Drives "app-enabled" + assign-to-me.
  user_id: string | null;
  postcode: string | null;
  payment_type: string | null;
  utr: string | null;
  active: boolean;
  source: string | null;
  image_file_id: string | null;
  // HR profile.
  ref: string | null;
  employment_type: string | null;
  primary_role: string | null;
  started_on: string | null;
  // Compliance (maintained in the worker's details).
  sentinel_number: string | null;
  sentinel_expiry: string | null;
  ncca_expiry: string | null;
  medical_expiry: string | null;
  oh_record_date: string | null;
  created_at: string;
}

export interface EmergencyContact {
  name?: string;
  relationship?: string;
  phone?: string;
  address?: string;
}

export interface Competence {
  id: string;
  // Card type / scheme (e.g. CSCS, NPORS, EUSR SHEA Power).
  category: string;
  alias: string | null;
  // Specific sub-category printed on the card (e.g. "Twin Lanyard").
  sub_category?: string | null;
  reference: string | null;
  // Awarding / issuing body (e.g. CITB, EUSR, NPORS).
  issuing_body?: string | null;
  issued?: string | null;
  expiry: string | null;
  // Attained Minimum-Core level (A/W/P/S) — optional (ADR 012).
  level?: string | null;
  // The card image(s) kept as evidence on the competence (file ids).
  front_file_id?: string | null;
  back_file_id?: string | null;
  // Office sign-off state: "pending" cards don't count toward the rosterable
  // gate until verified. Absent/"verified" counts (legacy + Sentinel).
  verification?: string | null;
  verified_by?: string | null;
  verified_at?: string | null;
  // Where the card came from: e.g. "sentinel" for synced on-track duties.
  source?: string | null;
  employer?: string | null;
  office?: string | null;
}

/** Curated UK rail/construction card schemes for the competence typeahead.
 *  Suggestions only — `category` stays free text so Sentinel-synced names and
 *  niche schemes still flow through the alias-reconciliation layer. */
export const CARD_TYPE_SUGGESTIONS = [
  "CSCS",
  "CCNSG (Safety Passport)",
  "EUSR SHEA Power",
  "EUSR SHEA Water",
  "EUSR SHEA Gas",
  "NRSWA Operative (S1/S3)",
  "NRSWA Supervisor (S5/S7)",
  "CPCS",
  "NPORS",
  "IPAF",
  "PASMA",
  "First Aid at Work",
  "Asbestos Awareness",
  "SSSTS",
  "SMSTS",
  "Driving Licence",
  "CAT & Genny",
  "SCO / Senior Cable Op",
] as const;

export interface MedicalRecord {
  id: string;
  level: string;
  provider: string | null;
  examined: string | null;
  expires: string | null;
  result: string | null;
}

// A single hard blocker / soft warning from the rosterable verdict.
export interface RosterFinding {
  layer: "minimum_core" | "sentinel" | "prerequisites";
  competency: string;
  reason: string;
  required?: string | null;
  held?: string | null;
  expiry?: string | null;
  days_left?: number | null;
  source?: string | null;
  note?: string | null;
}

export interface ProjectAssignment {
  id: string;
  project_id: string;
  project_name: string | null;
  role: string | null;
  started_on: string | null;
  ended_on: string | null;
  // The combined rosterable verdict (all three ADR-012 layers), server-derived.
  required?: CompetencyRequirement[];
  missing?: string[];
  rosterable?: boolean;
  blockers?: RosterFinding[];
  warnings?: RosterFinding[];
}

export interface DnaScreen {
  id: string;
  screen_type: string;
  provider: string | null;
  date: string | null;
  result: string | null;
}

export interface WorkerDocument {
  id: string;
  file_id: string;
  name: string;
  mime_type: string | null;
  size_bytes: number | null;
}

export interface WorkerAddress {
  id: string;
  type: string;              // home | hotel | alternate
  label: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  postcode: string;
  latitude: number | null;
  longitude: number | null;
  is_primary: boolean;
  accommodation_id: string | null;
}

export interface WorkerDetail extends WorkerRow {
  emergency_contact: EmergencyContact | null;
  image_file_id: string | null;
  competences: Competence[];
  medicals: MedicalRecord[];
  project_assignments: ProjectAssignment[];
  dna_screens: DnaScreen[];
  documents: WorkerDocument[];
  addresses: WorkerAddress[];
}

const workerEvent =
  (eventType: string) =>
  (workerId: string, payload: Record<string, unknown>): Promise<void> =>
    pushEvents([
      {
        aggregate_type: "worker",
        aggregate_id: workerId,
        event_type: eventType,
        payload,
      },
    ]);

export const deactivateWorker = (id: string): Promise<void> =>
  workerEvent("WorkerDeactivated")(id, {});
export const reactivateWorker = (id: string): Promise<void> =>
  workerEvent("WorkerReactivated")(id, {});

/** Save (upsert) a worker address — home / hotel / alternate, for travel time. */
export const saveWorkerAddress = (
  workerId: string,
  addr: Omit<WorkerAddress, "id"> & { id?: string },
): Promise<void> => {
  const { id, ...rest } = addr;
  return workerEvent("WorkerAddressSaved")(workerId, {
    address_id: id ?? crypto.randomUUID(),
    ...rest,
  });
};
export const removeWorkerAddress = (
  workerId: string,
  addressId: string,
): Promise<void> =>
  workerEvent("WorkerAddressRemoved")(workerId, { address_id: addressId });

/** Resolve a UK postcode to lat/long (postcodes.io) for a worker address. */
export const geocodePostcode = (
  postcode: string,
): Promise<{ latitude: number | null; longitude: number | null; formatted: string | null }> =>
  apiPost("/workers/geocode-postcode", { postcode });

// ---- Shared accommodation catalogue (reusable hotels / lodging) ----
export interface Accommodation {
  id: string;
  name: string;
  line1: string | null;
  line2: string | null;
  city: string | null;
  postcode: string;
  latitude: number | null;
  longitude: number | null;
  contact_phone: string | null;
  contact_email: string | null;
  notes: string | null;
  active: boolean;
}

export const getAccommodations = (): Promise<{ accommodations: Accommodation[] }> =>
  apiGet("/workers/accommodations");

export const saveAccommodations = async (
  accommodations: Accommodation[],
): Promise<{ accommodations: Accommodation[] }> => {
  const response = await apiFetch(`${env.apiBaseUrl}/workers/accommodations`, {
    method: "PUT",
    headers: {
      authorization: `Bearer ${await tokenProvider()}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({ accommodations }),
  });
  if (!response.ok) {
    throw new Error(`Saving accommodations failed (${response.status})`);
  }
  return response.json() as Promise<{ accommodations: Accommodation[] }>;
};

// ---- Transport modes (configurable travel-engine speeds) ----
export interface TransportMode {
  id: string;
  name: string;
  speed_mph: number;
  cost_per_mile: number | null;
  active: boolean;
}

export const getTransportModes = (): Promise<{ modes: TransportMode[] }> =>
  apiGet("/workers/transport-modes");

export const saveTransportModes = async (
  modes: TransportMode[],
): Promise<{ modes: TransportMode[] }> => {
  const response = await apiFetch(`${env.apiBaseUrl}/workers/transport-modes`, {
    method: "PUT",
    headers: {
      authorization: `Bearer ${await tokenProvider()}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({ modes }),
  });
  if (!response.ok) {
    throw new Error(`Saving transport modes failed (${response.status})`);
  }
  return response.json() as Promise<{ modes: TransportMode[] }>;
};

// ---- Work-location catalogue (reusable work sites with a risk weighting) ----
export type RiskLevel = "low" | "medium" | "high";
export interface WorkLocation {
  id: string;
  name: string;
  ref: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  postcode: string;
  latitude: number | null;
  longitude: number | null;
  risk_level: RiskLevel;
  risk_multiplier: number;
  geofence_radius_m: number | null;
  site_hours_start: string | null;
  site_hours_end: string | null;
  active: boolean;
}

export const getWorkLocations = (): Promise<{ locations: WorkLocation[] }> =>
  apiGet("/workers/work-locations");

export const saveWorkLocations = async (
  locations: WorkLocation[],
): Promise<{ locations: WorkLocation[] }> => {
  const response = await apiFetch(`${env.apiBaseUrl}/workers/work-locations`, {
    method: "PUT",
    headers: {
      authorization: `Bearer ${await tokenProvider()}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({ locations }),
  });
  if (!response.ok) {
    throw new Error(`Saving work locations failed (${response.status})`);
  }
  return response.json() as Promise<{ locations: WorkLocation[] }>;
};

// ---- Ad-hoc travel-time estimate (door-to-door distance/time/cost) ----
export interface TravelEstimate {
  distance_miles: number;
  travel_to_minutes: number;
  travel_from_minutes: number;
  total_travel_minutes: number;
  cost: number;
  mode_id: string;
  mode_name: string;
}

export const estimateTravel = (body: {
  origin_lat: number;
  origin_lng: number;
  dest_lat: number;
  dest_lng: number;
  mode_id?: string | null;
  return_journey?: boolean;
}): Promise<TravelEstimate> => apiPost("/workers/travel-estimate", body);

export const assignProject = (
  workerId: string,
  a: { project_id: string; project_name?: string; role?: string; started_on?: string },
): Promise<void> =>
  workerEvent("WorkerProjectAssigned")(workerId, {
    assignment_id: crypto.randomUUID(),
    ...a,
  });
export const endAssignment = (
  workerId: string,
  assignmentId: string,
  endedOn: string,
): Promise<void> =>
  workerEvent("WorkerProjectAssignmentEnded")(workerId, {
    assignment_id: assignmentId,
    ended_on: endedOn,
  });

// ---- Project team (bulk assign / unassign) ----

export interface TeamMember {
  worker_id: string;
  name: string;
  ncca_no: string | null;
  primary_role: string | null;
  image_file_id: string | null;
  assigned: boolean;
  assignment_id: string | null;
}

/** The tenant's active workers, flagged with whether each is on this project. */
export const getProjectTeam = (projectId: string): Promise<TeamMember[]> =>
  apiGet(`/projects/${projectId}/team`);

export interface ProjectStaffing {
  project_id: string;
  project_name: string;
  status: string;
  worker_count: number;
}

export interface StaffingSummary {
  projects: ProjectStaffing[];
  total_active_workers: number;
  assigned_workers: number;
  unassigned_workers: number;
  unassigned_worker_ids: string[];
}

/** Cross-project staffing — how many active workers are on each project. */
export const getStaffingSummary = (): Promise<StaffingSummary> =>
  apiGet("/projects/staffing");

/** Assign many workers to a project in one go (chunked into /sync batches). */
export async function bulkAssignProject(
  projectId: string,
  projectName: string | null,
  workerIds: string[],
  startedOn?: string,
): Promise<void> {
  for (let i = 0; i < workerIds.length; i += 100) {
    const chunk = workerIds.slice(i, i + 100);
    await pushEvents(
      chunk.map((workerId) => ({
        aggregate_type: "worker",
        aggregate_id: workerId,
        event_type: "WorkerProjectAssigned",
        payload: {
          assignment_id: crypto.randomUUID(),
          project_id: projectId,
          ...(projectName ? { project_name: projectName } : {}),
          ...(startedOn ? { started_on: startedOn } : {}),
        },
      })),
    );
  }
}

export const addDnaScreen = (
  workerId: string,
  d: Omit<DnaScreen, "id">,
): Promise<void> =>
  workerEvent("WorkerDnaAdded")(workerId, { dna_id: crypto.randomUUID(), ...d });
export const removeDnaScreen = (workerId: string, dnaId: string): Promise<void> =>
  workerEvent("WorkerDnaRemoved")(workerId, { dna_id: dnaId });

export const addWorkerDocument = (
  workerId: string,
  doc: Omit<WorkerDocument, "id">,
): Promise<void> =>
  workerEvent("WorkerDocumentAdded")(workerId, {
    document_id: crypto.randomUUID(),
    ...doc,
  });
export const removeWorkerDocument = (
  workerId: string,
  documentId: string,
): Promise<void> =>
  workerEvent("WorkerDocumentRemoved")(workerId, { document_id: documentId });

export interface CompetencyRequirement {
  competency: string;
  level: string;
}

export interface CompetencyRole {
  division: string;
  role: string;
  required: CompetencyRequirement[];
}

/** Roles that carry a competency requirement (drives the assign-role picker). */
export const listCompetencyRoles = (): Promise<CompetencyRole[]> =>
  apiGet("/workers/competency-roles");

export interface ComplianceStats {
  active_workers: number;
  cards_valid: number;
  cards_expiring: number;
  cards_expired: number;
  medicals_valid: number;
  medicals_expired: number;
  no_medical: number;
  all_clear: boolean;
}
export interface ComplianceCard {
  worker_id: string;
  worker_name: string;
  worker_ref: string | null;
  category: string | null;
  alias: string | null;
  reference: string | null;
  expiry: string | null;
  days_left: number | null;
}
export interface RoleSummary {
  blockers: number;
  expiring_soon: number;
  no_role_set: number;
  total: number;
}
export interface AssignmentCompliance {
  worker_id: string;
  worker_name: string;
  worker_ref: string | null;
  image_file_id: string | null;
  project_name: string | null;
  role: string | null;
  missing: string[];
  has_role: boolean;
  rosterable: boolean;
  blocker_count: number;
  warning_count: number;
}
export interface MedicalAttention {
  worker_id: string;
  worker_name: string;
  worker_ref: string | null;
  employment_type: string | null;
  role: string | null;
  last_medical: string | null;
  reason: string;
}
export interface ComplianceDashboard {
  stats: ComplianceStats;
  expired_cards: ComplianceCard[];
  expiring_cards: ComplianceCard[];
  role_summary: RoleSummary;
  assignments: AssignmentCompliance[];
  medicals_attention: MedicalAttention[];
}

export const getComplianceDashboard = (): Promise<ComplianceDashboard> =>
  apiGet("/workers/compliance");

// --- Fatigue & Risk Index (HSE RR446) ---
export interface FatigueAlert {
  date: string;
  code: string;
  level: string;
  message: string;
}
export interface WorkerFatigue {
  worker_id: string | null;
  ncca_no: string;
  name: string;
  duties: number;
  max_fatigue_index: number;       // 0..100 (% high KSS)
  max_risk_index: number;          // relative, ~1.0 = average
  peak_consecutive: number;
  alerts: FatigueAlert[];
}
export interface FatigueOverview {
  weeks: string[];
  policy: Record<string, number>;
  workers: WorkerFatigue[];
}

/** HSE Fatigue & Risk Index across the rostered workforce, computed from the
 *  roster over a window of rail weeks (default 3). Management only. */
export const getFatigueOverview = (weeks = 3): Promise<FatigueOverview> =>
  apiGet(`/workers/fatigue?weeks=${weeks}`);

export interface FatiguePolicy {
  // Breach limits.
  max_shift_hours: number;
  min_rest_hours: number;
  max_weekly_hours: number;
  max_consecutive_days: number;
  max_consecutive_nights: number;
  night_start_hour: number;
  night_end_hour: number;
  // FRI duty inputs (feed the duty-timing + job/breaks components).
  commute_minutes: number;
  workload: string;
  attention: string;
  longest_spell_minutes: number;
  break_minutes: number;
}

/** The tenant's configurable working-time limits the Criteria Alerts use. */
export const getFatiguePolicy = (): Promise<FatiguePolicy> =>
  apiGet("/workers/fatigue-policy");

export const saveFatiguePolicy = async (
  policy: FatiguePolicy,
): Promise<FatiguePolicy> => {
  const response = await apiFetch(`${env.apiBaseUrl}/workers/fatigue-policy`, {
    method: "PUT",
    headers: {
      authorization: `Bearer ${await tokenProvider()}`,
      "content-type": "application/json",
    },
    body: JSON.stringify(policy),
  });
  if (!response.ok) {
    throw new Error(`Saving fatigue limits failed (${response.status})`);
  }
  return response.json() as Promise<FatiguePolicy>;
};

// ---- Internal prerequisite gate (PR020, ADR 012 layer 2) ----
export interface PrerequisiteRule {
  competency: string;
  division?: string | null;
  role?: string | null;
  min_level?: string | null;
  note?: string | null;
}
export interface PrerequisiteGate {
  rules: PrerequisiteRule[];
  source?: string | null;
}

export const getPrerequisites = (): Promise<PrerequisiteGate> =>
  apiGet("/workers/prerequisites");

export const savePrerequisites = async (
  gate: PrerequisiteGate,
): Promise<PrerequisiteGate> => {
  const response = await apiFetch(`${env.apiBaseUrl}/workers/prerequisites`, {
    method: "PUT",
    headers: {
      authorization: `Bearer ${await tokenProvider()}`,
      "content-type": "application/json",
    },
    body: JSON.stringify(gate),
  });
  if (!response.ok) {
    throw new Error(`Saving prerequisites failed (${response.status})`);
  }
  return response.json() as Promise<PrerequisiteGate>;
};

// ---- Competence reconciliation (alias map) ----
export interface CompetenceAliasEntry {
  requirement: string;
  held_names: string[];
}
export interface CompetenceAliasMap {
  aliases: CompetenceAliasEntry[];
  source?: string | null;
}
export interface ReconCandidate {
  name: string;
  score: number;
}
export interface ReconRequirement {
  requirement: string;
  division: string | null;
  source: "matrix" | "prerequisite";
  matched: boolean;
  tracked: boolean;
  held_names: string[];
  candidates: ReconCandidate[];
}
export interface CompetenceReconciliation {
  requirements: ReconRequirement[];
  held_names: string[];
  unmatched: number;
  source?: string | null;
}

export const getCompetenceReconciliation = (): Promise<CompetenceReconciliation> =>
  apiGet("/workers/competence-reconciliation");

export const saveCompetenceAliases = async (
  map: CompetenceAliasMap,
): Promise<CompetenceAliasMap> => {
  const response = await apiFetch(`${env.apiBaseUrl}/workers/competence-aliases`, {
    method: "PUT",
    headers: {
      authorization: `Bearer ${await tokenProvider()}`,
      "content-type": "application/json",
    },
    body: JSON.stringify(map),
  });
  if (!response.ok) {
    throw new Error(`Saving aliases failed (${response.status})`);
  }
  return response.json() as Promise<CompetenceAliasMap>;
};

export const listWorkers = (active?: boolean): Promise<WorkerRow[]> =>
  apiGet(`/workers${active === undefined ? "" : `?active=${active}`}`);

// ---- Worker app activity + workforce map ----
export type ActivityBucket =
  | "on_site_today"
  | "within_7d"
  | "older"
  | "app"
  | "no_pin";
export interface WorkerActivityRow {
  worker_id: string;
  name: string;
  ncca_no: string;
  ref: string | null;
  image_file_id: string | null;
  last_seen_at: string | null;   // last app activity (ISO), null = never
  last_login: string | null;     // last Auth0 logon (ISO), web or mobile
  logins_count: number | null;   // total logons to date
  last_signon_on: string | null; // most recent GPS sign-on date (ISO date)
  latitude: number | null;
  longitude: number | null;
  // "signon" = GPS from a briefing sign-on; "app" = last-known app-use location.
  location_source: "signon" | "app" | null;
  bucket: ActivityBucket;
}
export interface WorkforceActivity {
  workers: WorkerActivityRow[];
  on_site_today: number;
  within_7d: number;
  with_pins: number;
  never_seen: number;
}

export const getWorkforceActivity = (): Promise<WorkforceActivity> =>
  apiGet("/workers/activity");

/** Best-effort last-seen ping (telemetry) — never throws into the UI. */
export async function pingAppActivity(platform = "web"): Promise<void> {
  try {
    await apiFetch(`${env.apiBaseUrl}/workers/me/seen`, {
      method: "POST",
      headers: {
        authorization: `Bearer ${await tokenProvider()}`,
        "content-type": "application/json",
      },
      body: JSON.stringify({ platform }),
    });
  } catch {
    /* telemetry is best-effort; ignore */
  }
}

/** Manually create a worker (keyed on NCCA). */
export const createWorker = (fields: {
  ncca_no: string;
  name: string;
  primary_role?: string;
}): Promise<{ id: string }> => apiPost("/workers", fields);

export interface Invitation {
  id: string;
  email: string;
  worker_name: string | null;
  site: string | null;
  expires_at: string;
  invited_by_name: string | null;
  status: string;
  created_at: string;
}

export const listInvitations = (): Promise<Invitation[]> =>
  apiGet("/invitations");

export const createInvitation = (fields: {
  email: string;
  worker_name?: string;
  site?: string;
}): Promise<Invitation> => apiPost("/invitations", fields);

export async function cancelInvitation(id: string): Promise<void> {
  const response = await apiFetch(`${env.apiBaseUrl}/invitations/${id}/cancel`, {
    method: "POST",
    headers: { authorization: `Bearer ${await tokenProvider()}` },
  });
  if (!response.ok) throw new Error(`Cancel returned ${response.status}`);
}

export const getWorker = (id: string): Promise<WorkerDetail> =>
  apiGet(`/workers/${id}`);

/** Edit a worker's details (partial; events flow through /sync/events). */
export const updateWorker = (
  id: string,
  fields: Record<string, unknown>,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "worker",
      aggregate_id: id,
      event_type: "WorkerUpdated",
      payload: fields,
    },
  ]);

export const addCompetence = (
  workerId: string,
  comp: Omit<Competence, "id">,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "worker",
      aggregate_id: workerId,
      event_type: "WorkerCompetenceAdded",
      payload: { competence_id: crypto.randomUUID(), ...comp },
    },
  ]);

/** Read a competency card's printed details from its photos (front + back,
 *  already uploaded with purpose `competence_card`) to pre-fill the add-
 *  competence form. Assisted entry — the office confirms before saving. */
export const extractCompetenceCard = (
  workerId: string,
  body: { front_file_id: string; back_file_id?: string },
): Promise<{
  card_type: string | null;
  category: string | null;
  reference: string | null;
  expiry: string | null;
  holder_name: string | null;
  model: string | null;
}> => apiPost(`/workers/${workerId}/competence-card-extract`, body);

export const removeCompetence = (
  workerId: string,
  competenceId: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "worker",
      aggregate_id: workerId,
      event_type: "WorkerCompetenceRemoved",
      payload: { competence_id: competenceId },
    },
  ]);

/** One competency card awaiting office sign-off, across the workforce. */
export interface PendingCard {
  worker_id: string;
  worker_name: string;
  worker_ref: string | null;
  competence_id: string;
  category: string;
  sub_category: string | null;
  reference: string | null;
  issuing_body: string | null;
  issued: string | null;
  expiry: string | null;
  front_file_id: string | null;
  back_file_id: string | null;
}

/** Every pending card across the active workforce — the office "to verify"
 *  queue (management only). Verify/reject each via verify/removeCompetence. */
export const getCompetenceVerificationQueue = (): Promise<PendingCard[]> =>
  apiGet("/workers/competence-verification-queue");

/** Office sign-off: flip a pending competence to verified so it counts toward
 *  the rosterable gate. The event log records who/when (audit trail). */
export const verifyCompetence = (
  workerId: string,
  competenceId: string,
  verifiedBy?: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "worker",
      aggregate_id: workerId,
      event_type: "WorkerCompetenceVerified",
      payload: { competence_id: competenceId, verified_by: verifiedBy ?? null },
    },
  ]);

export const addMedical = (
  workerId: string,
  med: Omit<MedicalRecord, "id">,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "worker",
      aggregate_id: workerId,
      event_type: "WorkerMedicalAdded",
      payload: { medical_id: crypto.randomUUID(), ...med },
    },
  ]);

export const removeMedical = (
  workerId: string,
  medicalId: string,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "worker",
      aggregate_id: workerId,
      event_type: "WorkerMedicalRemoved",
      payload: { medical_id: medicalId },
    },
  ]);

export interface NameSheetImportResult {
  workers_imported: number;
  skipped: number;
}

/** Upload the roster workbook and seed the worker master from its Name sheet. */
export async function importNameSheet(
  file: File,
): Promise<NameSheetImportResult> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiFetch(`${env.apiBaseUrl}/workforce/import/name-sheet`, {
    method: "POST",
    headers: { authorization: `Bearer ${await tokenProvider()}` },
    body: form,
  });
  if (!response.ok) {
    throw new Error(`Import failed (${response.status})`);
  }
  return response.json() as Promise<NameSheetImportResult>;
}

export interface RosterSummary {
  id: string;
  rail_week: string;
  period_start: string;
  period_end: string;
  created_at: string;
}

export const listRosters = (): Promise<RosterSummary[]> => apiGet("/rosters");

export interface RosterAssignment {
  assignment_id: string;
  ncca_no: string;
  name_as_rostered?: string | null;
  skill?: string | null;
  site_location?: string | null;
  // Pre-deployment competency check (server-derived).
  worker_found?: boolean;
  worker_id?: string;
  worker_role?: string | null;
  image_file_id?: string | null;
  has_role?: boolean;
  rosterable?: boolean | null;
  blockers?: RosterFinding[];
  warnings?: RosterFinding[];
}
export interface RosteringSummary {
  total: number;
  rosterable: number;
  not_rosterable: number;
  no_role: number;
  no_worker: number;
}
export interface RosterDetail extends RosterSummary {
  assignments: RosterAssignment[];
  rostering_summary: RosteringSummary | null;
}

export const getRoster = (id: string): Promise<RosterDetail> =>
  apiGet(`/rosters/${id}`);

/** Upload the roster workbook and import every week tab. */
export interface RosterImportResult {
  weeks_imported: number;
  assignments_imported: number;
  week_labels: string[];
  stub_workers_created: number;
}

export async function importRoster(file: File): Promise<RosterImportResult> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiFetch(`${env.apiBaseUrl}/workforce/import/roster`, {
    method: "POST",
    headers: { authorization: `Bearer ${await tokenProvider()}` },
    body: form,
  });
  if (!response.ok) throw new Error(`Roster import failed (${response.status})`);
  return response.json() as Promise<RosterImportResult>;
}

/** Upload the PR022 Minimum-Core Competency Matrix (one sheet per division). */
export interface CompetencyMatrixImportResult {
  divisions: string[];
  roles_imported: number;
  competencies: number;
}

export async function importCompetencyMatrix(
  file: File,
): Promise<CompetencyMatrixImportResult> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiFetch(
    `${env.apiBaseUrl}/workforce/import/competency-matrix`,
    {
      method: "POST",
      headers: { authorization: `Bearer ${await tokenProvider()}` },
      body: form,
    },
  );
  if (!response.ok) {
    throw new Error(`Matrix import failed (${response.status})`);
  }
  return response.json() as Promise<CompetencyMatrixImportResult>;
}

export interface SentinelImportResult {
  workers_synced: number;
  competences_synced: number;
  people_in_file: number;
  unmatched_sentinel_numbers: number;
}

export async function importSentinel(
  file: File,
): Promise<SentinelImportResult> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiFetch(`${env.apiBaseUrl}/workforce/import/sentinel`, {
    method: "POST",
    headers: { authorization: `Bearer ${await tokenProvider()}` },
    body: form,
  });
  if (!response.ok) {
    throw new Error(`Sentinel import failed (${response.status})`);
  }
  return response.json() as Promise<SentinelImportResult>;
}

// ---------- Timesheets ----------

export interface TimesheetSummary {
  id: string;
  worker_ncca_no: string;
  worker_name: string;
  rail_week: string;
  period_start: string;
  period_end: string;
  po_number: string | null;
  job_code: string | null;
  payment_type: string | null;
  planned_hours: number | null;
  adjusted_hours: number | null;
  status: string;
  approved_at: string | null;
  created_at: string;
}

export interface CostReportRow {
  job_code: string | null;
  worker_count: number;
  timesheet_count: number;
  approved_count: number;
  planned_hours: number;
  actual_hours: number;
  variance_hours: number;
  confirmed_hours: number;
  rate: number | null;
  currency: string;
  planned_cost: number | null;
  cost: number | null;
  variance_cost: number | null;
  confirmed_cost: number | null;
}

/** Hours + cost grouped by job/invoicing code — the per-job cost report. */
export const listCostReport = (railWeek?: string): Promise<CostReportRow[]> =>
  apiGet(
    `/timesheets/cost-report${
      railWeek ? `?rail_week=${encodeURIComponent(railWeek)}` : ""
    }`,
  );

/** Set the charge rate (per hour) for a job/invoicing code. */
export const setJobRate = (
  jobCode: string,
  rate: number,
): Promise<{ job_code: string; rate: number; currency: string }> =>
  apiPost("/timesheets/job-rates", { job_code: jobCode, rate });

export interface Reconciliation {
  planned_hours: number | null;
  actual_hours: number;
  adjusted_hours: number | null;
  reconciled_hours: number | null;
  variance: number | null;
  has_signons: boolean;
  actual_days: Array<{
    date: string;
    time_in: string | null;
    time_out: string | null;
    hours: number | null;
    project_name: string | null;
    latitude: number | null;
    longitude: number | null;
    distance_m: number | null;
    on_site: boolean | null;
    out_of_hours: boolean | null;
  }>;
  planned_shifts: Array<Record<string, unknown>>;
  geofence_checked: boolean;
  geofence_radius_m: number | null;
  located_days: number;
  on_site_days: number;
  off_site_days: number;
  site_hours_start: string | null;
  site_hours_end: string | null;
  out_of_hours_days: number;
}

export interface TimesheetDetail extends TimesheetSummary {
  adjustment_note: string | null;
  roster_id: string | null;
  reconciliation: Reconciliation;
}

export const listTimesheets = (railWeek?: string): Promise<TimesheetSummary[]> =>
  apiGet(`/timesheets${railWeek ? `?rail_week=${encodeURIComponent(railWeek)}` : ""}`);

export const getTimesheet = (id: string): Promise<TimesheetDetail> =>
  apiGet(`/timesheets/${id}`);

export interface GenerateTimesheetsResult {
  generated: number;
  rail_week: string;
}

export const generateTimesheets = (
  rosterId: string,
): Promise<GenerateTimesheetsResult> =>
  apiPost("/timesheets/generate", { roster_id: rosterId });

export async function fetchTimesheetPdf(id: string): Promise<Blob> {
  const response = await apiFetch(`${env.apiBaseUrl}/timesheets/${id}/pdf`, {
    headers: { authorization: `Bearer ${await tokenProvider()}` },
  });
  if (!response.ok) throw new Error(`PDF returned ${response.status}`);
  return response.blob();
}

/** Payroll/accounts CSV of the timesheets (optionally a single rail week). */
export async function fetchTimesheetsCsv(railWeek?: string): Promise<Blob> {
  const q = railWeek ? `?rail_week=${encodeURIComponent(railWeek)}` : "";
  const response = await apiFetch(`${env.apiBaseUrl}/timesheets/export.csv${q}`, {
    headers: { authorization: `Bearer ${await tokenProvider()}` },
  });
  if (!response.ok) throw new Error(`CSV export returned ${response.status}`);
  return response.blob();
}

// ---------- Document library (project + org-wide) ----------

export interface LibraryDocument {
  id: string;
  scope: string;
  project_id: string | null;
  visibility: string;
  kind: string;
  title: string;
  file_id: string | null;
  url: string | null;
  added_by_name: string | null;
  added_at: string;
  acknowledgement_required: boolean;
  acknowledged_count: number;
  acknowledged_by_me: boolean;
  required_total: number | null;
}

export interface LibraryAckPerson {
  name: string | null;
  email: string | null;
  acknowledged_at: string | null;
}
export interface LibraryAckStatus {
  acknowledged: LibraryAckPerson[];
  outstanding: LibraryAckPerson[];
}
export const getLibraryAcknowledgements = (
  id: string,
): Promise<LibraryAckStatus> =>
  apiGet(`/library-documents/${id}/acknowledgements`);

export const listLibraryDocuments = (
  projectId?: string | null,
  scope?: "project" | "org",
): Promise<LibraryDocument[]> => {
  const params = new URLSearchParams();
  if (projectId) params.set("project_id", projectId);
  if (scope) params.set("scope", scope);
  const qs = params.toString();
  return apiGet(`/library-documents${qs ? `?${qs}` : ""}`);
};

export const addLibraryDocument = (doc: {
  scope: "project" | "org";
  visibility: "all" | "office";
  kind: string;
  title: string;
  projectId?: string;
  fileId?: string;
  url?: string;
  acknowledgementRequired?: boolean;
}): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "library_document",
      aggregate_id: crypto.randomUUID(),
      event_type: "LibraryDocumentAdded",
      payload: {
        scope: doc.scope,
        visibility: doc.visibility,
        kind: doc.kind,
        title: doc.title,
        added_at: new Date().toISOString(),
        ...(doc.scope === "project" && doc.projectId
          ? { project_id: doc.projectId }
          : {}),
        ...(doc.fileId ? { file_id: doc.fileId } : {}),
        ...(doc.url ? { url: doc.url } : {}),
        ...(doc.acknowledgementRequired
          ? { acknowledgement_required: true }
          : {}),
      },
    },
  ]);

export const removeLibraryDocument = (id: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "library_document",
      aggregate_id: id,
      event_type: "LibraryDocumentRemoved",
      payload: {},
    },
  ]);

/** The current user confirms they've read + understood the document. */
export const acknowledgeLibraryDocument = (id: string): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "library_document",
      aggregate_id: id,
      event_type: "LibraryDocumentAcknowledged",
      payload: { acknowledged_at: new Date().toISOString() },
    },
  ]);

/** Toggle the "must be acknowledged" flag on an existing document. */
export const setLibraryDocAckRequired = (
  id: string,
  required: boolean,
): Promise<void> =>
  pushEvents([
    {
      aggregate_type: "library_document",
      aggregate_id: id,
      event_type: "LibraryDocumentAckRequirementSet",
      payload: { required },
    },
  ]);

// ---------- File upload / download ----------

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

interface UploadIntent {
  file_id: string;
  upload_url: string;
  headers: Record<string, string>;
}

/**
 * Upload a file via a short-lived presigned S3 URL and return its
 * file_id. The API never proxies the bytes — they go straight to S3.
 */
export async function uploadFile(
  file: File,
  purpose: string,
): Promise<string> {
  const buffer = await file.arrayBuffer();
  const intent = await apiPost<UploadIntent>("/files/upload-intent", {
    // Strip codec params (e.g. "audio/webm;codecs=opus" from MediaRecorder) —
    // the server allowlists bare media types.
    mime_type: (file.type || "application/octet-stream").split(";")[0],
    size_bytes: file.size,
    sha256: await sha256Hex(buffer),
    purpose,
  });
  const put = await window.fetch(intent.upload_url, {
    method: "PUT",
    headers: intent.headers,
    body: buffer,
  });
  if (!put.ok) {
    throw new Error(`File upload failed (${put.status})`);
  }
  return intent.file_id;
}

/** SHA-256 (hex) of a file's contents — needed when an attach event records
 *  the file's digest (e.g. diary photos) alongside the uploaded file id. */
export async function sha256OfFile(file: File): Promise<string> {
  return sha256Hex(await file.arrayBuffer());
}

/** Resolve a short-lived presigned download URL for a stored file. */
export async function getDownloadUrl(fileId: string): Promise<string> {
  const res = await apiGet<{ url: string }>(
    `/files/${fileId}/download-url`,
  );
  return res.url;
}

// ---- App users (Auth0-backed; management only) ----

export interface AppUser {
  user_id: string;
  email: string | null;
  name: string | null;
  roles: string[];
  project_ids: string[];
  last_login: string | null;
  logins_count: number | null;
  created_at: string | null;
  blocked: boolean;
}

export interface InviteResult {
  user_id: string;
  password_setup_url: string;
}

/** The tenant's app users (logins) with roles + project access. */
export const listUsers = (): Promise<AppUser[]> => apiGet("/users");

/** Set a user's roles (applies on their next login). */
export const updateUserRoles = (
  userId: string,
  roles: string[],
): Promise<AppUser> => apiPatch(`/users/${userId}/roles`, { roles });

/** Invite a non-worker user (office/CRE/admin); returns a set-password URL. */
export async function inviteUser(body: {
  email: string;
  name?: string;
  roles: string[];
}): Promise<InviteResult> {
  const response = await apiFetch(`${env.apiBaseUrl}/users/invite`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${await tokenProvider()}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await detailedError(response, "/users/invite");
  return response.json() as Promise<InviteResult>;
}

// ---- Self-service ----

/**
 * Trigger Auth0's self-service password reset — emails the signed-in user a
 * one-time link to set a new password. Hits the Auth0 Authentication API
 * directly (no backend / Management scopes needed); keyed by client_id + email.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const response = await fetch(
    `https://${env.auth0Domain}/dbconnections/change_password`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        client_id: env.auth0ClientId,
        email,
        connection: env.auth0DbConnection,
      }),
    },
  );
  if (!response.ok) {
    throw new Error(`Couldn't start password reset (${response.status})`);
  }
}
